/**
 * Comprehensive Groundworks Excel Export
 * Exports all bill summaries to Excel with proper formatting
 */

import { generateBOQSummaryWithCategories } from "./boqSummaryGenerator";
import { STEP_CATEGORIES } from "./billSummaryCategories";
import { reconstructPlotDrivesColumnHeaders } from "./plotDrivesColumnMapping";
import { reconstructPlotPavingsColumnHeaders } from "./plotPavingsColumnMapping";
import { formatDimensionLabel } from "./dimensionLabel";
import {
  calculateStepGrandTotal,
  calculateFoundationsGrandTotal,
  calculateCutFillGrandTotal,
  calculateDrainageGrandTotal,
  calculatePlotDrivesGrandTotal,
  calculatePlotServiceGrandTotal,
  calculatePlotPavingsGrandTotal,
  calculateScaffoldGrandTotal,
  calculateGardenGrandTotal,
  calculateRetScreenWallsGrandTotal,
  calculateGEOToberWallGrandTotal,
} from "./summaryCalculations";
import { MNJLOGOBASE64 } from "./constants";
import {
  createCoverSheet,
  createEndCover,
  createEndCoverSimple,
  CoverSheetData,
  LocationData,
} from "./excelCoverSheet";
import {
  generatePlotMatrixCategoryColumns,
  generatePlotMatrixCategoryRows,
  computeSoilBoQTotals,
  calculateRowTotal,
  INCLUDED_SUB_STAGES,
  computeCutFillFieldBillSummaryData,
} from "./plotMatrixCategoryGenerator";
import type { BoqStepTargets } from "./plotMatrixCategoryGenerator";
import { createNotesSheet } from "./notesSheetGenerator";
import {
  autoFitColumns,
  createWorksheetHeader,
  styleColumnHeaders,
  styleDataRow,
  styleCategoryHeaderRow,
  styleTotalRow,
} from "./excelBoqSheetStyling";
import { RateMappingJSON } from "../types/rateMappingTypes";
import { ItemLibraryResponse } from "../api/itemLibrary";
import { RetainingWallsBuildUpSpecification } from "../api/retainingWallsBuildUp";
import { GeoToberWallBuildUpSpecification } from "../api/wallSectionBuildUp";
import { createFoundationsRateLookupMap, applyFoundationsRatesToSummary } from "./foundationsItemLibraryIntegration";
import { createCutFillsRateLookupMap, applyCutFillsRatesToSummary } from "./cutFillsItemLibraryIntegration";
import type { DynamicBillSummaryTab, DynamicBillSummaryNode } from "../types/tender.types";
import { computeDynamicBillSummaryNodeAmount, computeDynamicBillSummaryNodeRate } from "./dynamicBillSummarySerialization";
import { calculateSumAnalysisGrandTotal } from "./sumAnalysisGrandTotalCalculator";
import { applyDynamicBillSummaryToPlotMatrixRows } from "./dynamicBillSummaryPlotMatrix";

/**
 * Effective markup percentages applied to the Plot Matrix Excel sheet.
 * Callers pre-resolve include-flag × percentage so a disabled markup is
 * passed as 0 here — the export logic doesn't need to know about the
 * localStorage toggle. `upliftFactor = (1 + ohpPct/100) * (1 + prelimsPct/100)`
 * is applied per cell (matches the UI's `displayPlotMatrixData`).
 * `discountPct` adds a DISCOUNT row + TENDER TOTAL row below GRAND TOTAL.
 */
export interface PlotMatrixMarkup {
  prelimsPct: number;
  ohpPct: number;
  discountPct: number;
}

// Descriptions matching the column titles in CutFills.tsx exactly (mirrors GroundworksCutFillBillSummary.tsx)
const CUT_FILL_FIELD_DESCRIPTIONS: Record<string, string> = {
  vegStrip: 'Site strip to remove vegetation',
  topsoilStrip: 'Excavate to strip topsoil, depth n.e. xxmm',
  topsoilDisposalOnsite: 'Topsoil Disposal onsite',
  topsoilDisposalOffsite: 'Topsoil Disposal offsite',
  cutFounds: 'Excavate to reduce level and deposit on site',
  fillFounds: 'Fill to make up levels with site won sub-soil',
  cutPaths: 'Excavate to reduce level and deposit on site',
  fillPaths: 'Fill to make up levels with site won sub-soil',
  cutGarden: 'Excavate to reduce level and deposit on site',
  fillGarden: 'Fill to make up levels with site won sub-soil',
  cutDrives: 'Excavate to reduce level and deposit on site',
  fillDrives: 'Fill to make up levels with site won sub-soil',
  fillToMakeUp6F2: 'Fill to make up levels with 6F2',
};

// Maps each Bill Summary sheet's `section.stepName` to the `groundworksData`
// field holding its ad-hoc "Add Section"/"Add Item" rows (useBillSummaryExtraItems.tsx)
// — the FIXED-summary counterpart of a Dynamic Bill Summary tab's own `nodes`
// tree, keyed by attachment point (`"__root__"` = top-level, otherwise an
// existing row's own key). See GroundworksData in tender.types.ts for the
// full doc comment on each field.
const EXTRA_ITEMS_FIELD_BY_STEP_NAME: Record<string, string> = {
  "Foundations": "foundationsExtraItems",
  "Cut & Fills": "cutFillsExtraItems",
  "Drainage": "drainageExtraItems",
  "Plot Drives": "plotDrivesExtraItems",
  "Plot Service": "plotServiceExtraItems",
  "Plot Pavings": "plotPavingsExtraItems",
  "Scaffold": "scaffoldExtraItems",
  "Garden": "gardenExtraItems",
  "Ret & Screen Walls": "retainingWallsExtraItems",
  "Geowall & Tobermore Walls": "geoWallsExtraItems",
};

/** Maps a Cut & Fill field name to its column-type sub-section (mirrors UI getColumnType). */
function getCutFillColumnType(fieldName: string): string {
  const lower = fieldName.toLowerCase();
  if (lower.includes('drives') || lower.includes('6f2')) return 'Drives';
  if (lower.includes('founds')) return 'Founds';
  if (lower.includes('paths')) return 'Paths';
  if (lower.includes('garden')) return 'Garden';
  if (lower.includes('pos')) return 'POS';
  return 'Other';
}
import { createDrainageRateLookupMap, applyDrainageRatesToSummary } from "./drainageItemLibraryIntegration";
import {
  OSMA_FOUL_COMBINED_FIELDS,
  OSMA_STORM_COMBINED_FIELDS,
  POLYPIPE_FOUL_COMBINED_FIELDS,
  POLYPIPE_STORM_COMBINED_FIELDS,
} from "./drainageReferenceMapping";
import { createPlotDrivesRateLookupMap, applyPlotDrivesRatesToSummary } from "./plotDrivesItemLibraryIntegration";
import { createPlotServiceRateLookupMap, applyPlotServiceRatesToSummary } from "./plotServiceItemLibraryIntegration";
import { createPlotPavingsRateLookupMap, applyPlotPavingsRatesToSummary } from "./plotPavingsItemLibraryIntegration";
import { createScaffoldRateLookupMap, applyScaffoldRatesToSummary } from "./scaffoldItemLibraryIntegration";
import { createGardenRateLookupMap, applyGardenRatesToSummary } from "./gardenItemLibraryIntegration";
import {
  wallSpecifications,
  WALLS_BUILD_UP_ITEM_CODE_MAP,
  resolveWallsBuildUpItemCode,
  isFacingBrickworkItem,
  isBlockworkBuildUpItem,
  getWallsBuildUpRateMultiplier,
} from "../mock/walls-section-build-up";
import { SCREEN_WALL_BOQ_FIELDS, getScreenWallFieldCode } from "./retainingWallsReferenceMapping";
import {
  geoToberWallSpecifications,
  geoToberWallFieldToSpecId,
  GEO_WALL_BUILD_UP_ITEM_CODE_MAP,
  TOBERMORE_BUILD_UP_ITEM_CODE_MAP,
} from "../mock/geo-tober-wall-build-up";

/**
 * Generate retaining walls BOQ items using collective bill format.
 * Matches RetScreenWallsBillSummary (collectiveProcessedData): one row per wall spec,
 * rate = netTotal * (1 + ohp%), quantity in metres.
 */
function generateRetWallsCollectiveItems(groundworksData: any, itemLibraryData: any, _settings: any): any[] {
  const retainingWallsData = groundworksData?.retainingWallsTable || [];
  if (retainingWallsData.length === 0) return [];

  const libRateMap = new Map<string, number>();
  (Array.isArray(itemLibraryData) ? itemLibraryData : []).forEach((lib: any) => {
    const code = lib.resourceCode?.trim().toUpperCase();
    if (code) libRateMap.set(code, lib.nettRate ?? 0);
  });
  const getRateFromLibrary = (evaluateCode: string): number =>
    libRateMap.get(evaluateCode.trim().toUpperCase()) ?? 0;

  const wallsBuildUpData = (groundworksData.wallsBuildUpData || {}) as Record<string, any[]>;
  const wallsBuildUpDimensions = (groundworksData.wallsBuildUpDimensions || {}) as Record<string, Array<{ id?: string; description: string; net: number; unit: string; rate?: number }>>;

  // Total metres per wall spec from take-off
  const wallTypeTotals: Record<string, number> = {};
  retainingWallsData.forEach((row: any) => {
    wallSpecifications.forEach((spec: any) => {
      wallTypeTotals[spec.id] = (wallTypeTotals[spec.id] || 0) + ((row[spec.id] as number) || 0);
    });
  });

  const items: any[] = [];

  // Screen Walls — five standalone fields from the take-off table, priced at
  // the base library rate. Markup (OH&P + prelims) is applied uniformly in the
  // normalization pass inside `createBillSummarySheet`.
  // Screen Walls / EO Pillar dropdowns drive which evaluate code is used for
  // the `screenWalls`/`screenWallsEO` fields (each independently), matching
  // the UI's Configure modal.
  const screenWallsLabel: string | undefined =
    (groundworksData as any)?.wallsBuildUpConfig?.screenWallsLabel;
  const eoPillarLabel: string | undefined =
    (groundworksData as any)?.wallsBuildUpConfig?.eoPillarLabel;
  SCREEN_WALL_BOQ_FIELDS.forEach((field) => {
    const qty = retainingWallsData.reduce(
      (sum: number, row: any) => sum + (Number(row[field.id]) || 0),
      0,
    );
    if (qty <= 0) return;
    const baseRate = getRateFromLibrary(getScreenWallFieldCode(field, screenWallsLabel, eoPillarLabel));
    const roundedQty = Math.round(qty);
    items.push({
      key: `screen-walls-${field.id}`,
      fieldName: field.id,
      category: 'Screen Walls',
      description: field.label,
      quantity: roundedQty,
      unit: field.unit,
      rate: baseRate,
      amount: roundedQty * baseRate,
    });
  });

  // Facing Brickwork dropdown drives which evaluate code is used for
  // `facing_bricks_*_wide` rows. Matches the UI's selection logic so the export
  // tracks the variant picked in the Configure modal.
  const facingBrickworkLabel: string | undefined =
    (groundworksData as any)?.wallsBuildUpConfig?.facingBrickworkLabel;

  // Flat description → mock id map across ALL wall specs (matches the UI's
  // descriptionToMockId map in `RetScreenWallsBillSummary.tsx`).
  const descriptionToMockId = new Map<string, string>();
  wallSpecifications.forEach((spec: any) =>
    spec.items.forEach((mi: any) =>
      descriptionToMockId.set(mi.description.toLowerCase(), mi.id),
    ),
  );

  wallSpecifications
    .filter((spec: any) => (wallTypeTotals[spec.id] || 0) > 0)
    .forEach((spec: any) => {
      const savedItems = wallsBuildUpData[spec.id] || [];
      const wallQtyM = wallTypeTotals[spec.id] || 0;
      const dimensionItems = wallsBuildUpDimensions[spec.id];
      const itemsToUse = dimensionItems && dimensionItems.length > 0 ? dimensionItems : [];

      const netTotal = itemsToUse.reduce((sum: number, item: any) => {
        const descKey = (item.description || '').toLowerCase();
        const mockId = descriptionToMockId.get(descKey);
        const persistedId = item.id || '';
        const derivedId =
          persistedId || (mockId ?? descKey.replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, ''));
        const isFacingBrick = isFacingBrickworkItem(derivedId);
        const evaluateCode = isFacingBrick
          ? (resolveWallsBuildUpItemCode(derivedId, facingBrickworkLabel) || '')
          : (WALLS_BUILD_UP_ITEM_CODE_MAP[derivedId] || '');
        const libraryRate = evaluateCode ? getRateFromLibrary(evaluateCode) : 0;
        const savedItem = savedItems.find((s: any) => s.id === derivedId);
        // Facing-brickwork half-brick widths and 100mm-blockwork course counts
        // both re-derive `libraryRate × N` so stale persisted snapshots can't
        // lock the row to a pre-multiplier rate — see `WALLS_BUILD_UP_RATE_MULTIPLIERS`.
        const isMultiplied = isBlockworkBuildUpItem(derivedId);
        const rateMultiplier = getWallsBuildUpRateMultiplier(derivedId);
        const baseRate = isMultiplied
          ? libraryRate * rateMultiplier
          : (savedItem && savedItem.rate !== 0 ? savedItem.rate : libraryRate);
        return sum + Math.abs((item.net || 0) * baseRate);
      }, 0);

      // Base rate only — markup applied uniformly in createBillSummarySheet normalization pass.
      const ratePerM = netTotal;
      const roundedWallQtyM = Math.round(wallQtyM);
      const amount = roundedWallQtyM * ratePerM;

      items.push({
        key: spec.id,
        fieldName: spec.id,
        category: spec.category,
        description: spec.name,
        quantity: roundedWallQtyM,
        unit: "m",
        rate: ratePerM,
        amount,
      });
    });

  return items;
}

/**
 * Generate Geowall & Tobermore Walls BOQ items using collective bill format.
 * Matches GEOToberWallBillSummary (collectiveProcessedData): one row per spec,
 * rate = netTotal * (1 + ohp%), quantity in metres.
 */
function generateGeoWallsCollectiveItems(groundworksData: any, itemLibraryData: any, _settings: any): any[] {
  const tableData = groundworksData?.geoToberWallTable || [];
  if (tableData.length === 0) return [];

  // Inverted map: specId -> fieldName
  const specIdToField: Record<string, string> = {};
  Object.entries(geoToberWallFieldToSpecId).forEach(([field, specId]) => {
    specIdToField[specId] = field;
  });

  const libRateMap = new Map<string, number>();
  (Array.isArray(itemLibraryData) ? itemLibraryData : []).forEach((lib: any) => {
    const code = lib.resourceCode?.trim().toUpperCase();
    if (code) libRateMap.set(code, lib.nettRate ?? 0);
  });
  const getRateFromLibrary = (evaluateCode: string): number =>
    libRateMap.get(evaluateCode.trim().toUpperCase()) ?? 0;

  const buildUpData = (groundworksData.geoToberWallBuildUpData || {}) as Record<string, any[]>;
  const geoToberWallBuildUpDimensions = (groundworksData.geoToberWallBuildUpDimensions || {}) as Record<string, Array<{ id?: string; description: string; net: number; unit: string; rate?: number }>>;

  // Total metres per spec from take-off
  const wallTypeTotals: Record<string, number> = {};
  tableData.forEach((row: any) => {
    Object.entries(geoToberWallFieldToSpecId).forEach(([fieldName, specId]) => {
      wallTypeTotals[specId] = (wallTypeTotals[specId] || 0) + ((row[fieldName] as number) || 0);
    });
  });

  // Dynamic Tobermore column headers from groundworks state
  const tpcwColumnHeaders: Record<string, string> = (groundworksData as any)?.geoToberWallColumnHeaders || {};
  const tpcwDimensionValues: Record<string, string> = (groundworksData as any)?.geoToberWallDimensionValues || {};
  const getTpcwLabel = (specId: string, fallback: string): string => {
    const match = specId.match(/^tobermore_(tpcw\d+)$/);
    if (!match) return fallback;
    const key = match[1];
    // Return stored column header (from Excel import) exactly as-is.
    // Fallback: build label from dimension value.
    if (tpcwColumnHeaders[key]) return tpcwColumnHeaders[key];
    if (tpcwDimensionValues[key]) return `TPCW ${formatDimensionLabel(tpcwDimensionValues[key])}mm`;
    return fallback;
  };

  const items: any[] = [];
  geoToberWallSpecifications
    .filter((spec) => (wallTypeTotals[spec.id] || 0) > 0)
    .forEach((spec) => {
      const savedItems = buildUpData[spec.id] || [];
      const wallQtyM = wallTypeTotals[spec.id] || 0;
      const codeMap = spec.type === "geowall" ? GEO_WALL_BUILD_UP_ITEM_CODE_MAP : TOBERMORE_BUILD_UP_ITEM_CODE_MAP;
      const dimensionItems = geoToberWallBuildUpDimensions[spec.id];
      const itemsToUse = dimensionItems && dimensionItems.length > 0 ? dimensionItems : [];

      // Description → mock id map scoped to this spec's `type` — mirrors the UI
      // logic in `GEOToberWallBillSummary.tsx`. The UI then resolves baseRate as
      // `savedItem.rate || libraryRate` (it does NOT fall back to the persisted
      // item's rate field), so we must do the same to keep the BOQ export's
      // rate values identical to what the UI displays.
      const descriptionToMockId = new Map<string, string>();
      geoToberWallSpecifications.forEach((s) => {
        if (s.type === spec.type) {
          s.items.forEach((mi: any) => descriptionToMockId.set(mi.description.toLowerCase(), mi.id));
        }
      });

      const netTotal = itemsToUse.reduce((sum, item) => {
        const descKey = (item.description || '').toLowerCase();
        const mockId = descriptionToMockId.get(descKey);
        const derivedId = mockId ?? descKey.replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
        const evaluateCode = codeMap[derivedId] || '';
        const libraryRate = evaluateCode ? getRateFromLibrary(evaluateCode) : 0;
        const savedItem = savedItems.find((s: any) => s.id === derivedId);
        const baseRate = savedItem && savedItem.rate !== 0 ? savedItem.rate : libraryRate;
        return sum + Math.abs((item.net || 0) * baseRate);
      }, 0);

      // Base rate only — markup applied uniformly in createBillSummarySheet normalization pass.
      const ratePerM = netTotal;
      const roundedWallQtyM = Math.round(wallQtyM);
      const amount = roundedWallQtyM * ratePerM;

      items.push({
        key: spec.id,
        fieldName: spec.id,
        category: spec.category,
        description: getTpcwLabel(spec.id, spec.name),
        quantity: roundedWallQtyM,
        unit: "m",
        rate: ratePerM,
        amount,
      });
    });

  return items;
}

/**
 * Generate Drainage BOQ items matching DrainageBillSummary exactly.
 * Detects OSMA vs Polypipe and builds FOUL / STORM sections with per-plot rows.
 */
function generateDrainageBillSummaryItems(groundworksData: any, itemLibraryData: any): any[] {
  const drainageRows: any[] =
    groundworksData?.drainageTableStandard || groundworksData?.drainageTable || [];
  const osmaConfig = groundworksData?.drainageDimensionValues || {};
  const polypipeConfig = groundworksData?.drainagePolypipeConfig || {};

  if (drainageRows.length === 0) return [];

  const rateLookupMap = createDrainageRateLookupMap(itemLibraryData, groundworksData);

  const sumField = (field: string): number =>
    drainageRows.reduce((acc: number, row: any) => acc + (parseFloat(row[field]) || 0), 0);
  const getRateInfo = (fieldName: string) => (rateLookupMap as any).get(fieldName);
  const getRate = (fieldName: string): number => getRateInfo(fieldName)?.rate || 0;
  // Mirrors DrainageBillSummary.tsx — description and unit come from the item
  // library; fall back to the hardcoded string only when no library entry exists.
  const getDescription = (fieldName: string, fallback: string): string =>
    getRateInfo(fieldName)?.description || fallback;
  const getUnit = (fieldName: string, fallback: string): string =>
    getRateInfo(fieldName)?.unit || fallback;

  const hasOsma = drainageRows.some((r: any) => (parseFloat(r.osma32mmBend) || 0) > 0 || (parseFloat(r.osmaStorm32mmBend) || 0) > 0);
  const hasPolypipe = drainageRows.some((r: any) => (parseFloat(r.polypipe32mmBend) || 0) > 0 || (parseFloat(r.polypipeStorm32mmBend) || 0) > 0);
  const usePolypipe = hasPolypipe && !hasOsma;

  // Mirrors DrainageBillSummary.tsx buildPerPlotRow exactly:
  //   combinedSum = Σ_field (sumField(field) × rate(field)) over ALL combinedFields
  //   rate        = combinedSum ÷ nrPlotsTakeOff  (then round to 2dp)
  //   amount      = Math.round(totalPlots) × parseFloat(rate.toFixed(2))
  //   description = item-library description for labField, else fallback
  const buildPerPlotRow = (
    itemNum: string,
    desc: string,
    labField: string,
    combinedFields: readonly string[],
    category: string,
    totalPlots: number,
    nrPlotsTakeOff: number,
  ) => {
    const combinedSum = combinedFields.reduce((sum, f) => sum + sumField(f) * getRate(f), 0);
    const rate = nrPlotsTakeOff > 0 ? combinedSum / nrPlotsTakeOff : 0;
    const roundedPlots = Math.round(totalPlots);
    const amount = roundedPlots * rate;
    const description = "Plot Drainage";
    return { key: itemNum, fieldName: labField, category, description, quantity: roundedPlots, unit: "plot", rate, amount };
  };

  // Mirrors DrainageBillSummary.tsx buildItemRow exactly:
  //   amount       = Math.round(quantity) × rate (markup applied later in normalization pass)
  //   description  = item-library description for fieldName, else fallback
  //   unit         = item-library unit for fieldName, else fallback
  const buildItemRow = (itemNum: string, desc: string, fieldName: string, unit: string, category: string, qty?: number) => {
    const quantity = qty !== undefined ? qty : sumField(fieldName);
    const rate = getRate(fieldName);
    const roundedQty = Math.round(quantity);
    const description = getDescription(fieldName, desc);
    const resolvedUnit = getUnit(fieldName, unit);
    return { key: itemNum, fieldName, category, description, quantity: roundedQty, unit: resolvedUnit, rate, amount: roundedQty * rate };
  };

  if (usePolypipe) {
    const totalPlots = parseFloat(polypipeConfig.totalPlots) || 0;
    const nrPlotsTakeOff = parseFloat(polypipeConfig.nrPlotsTakeOff) || 0;
    return [
      buildPerPlotRow("1.0", "Foul – Polypipe", "polypipe32mmBend", POLYPIPE_FOUL_COMBINED_FIELDS, "Polypipe-Foul", totalPlots, nrPlotsTakeOff),
      buildItemRow("1.1", "NME PPIC", "polypipeNmePpic", "Nr", "Polypipe-Foul"),
      buildItemRow("1.2", "600mm NME PPIC", "polypipe600mmNmePpic", "Nr", "Polypipe-Foul"),
      buildPerPlotRow("2.0", "Storm – Polypipe", "polypipeStorm32mmBend", POLYPIPE_STORM_COMBINED_FIELDS, "Polypipe-Storm", totalPlots, nrPlotsTakeOff),
      buildItemRow("2.1", "NME PPIC", "polypipeStormNmePpic", "Nr", "Polypipe-Storm"),
      buildItemRow("2.2", "600mm NME PPIC", "polypipeStorm600mmNmePpic", "Nr", "Polypipe-Storm"),
      buildItemRow("2.3", "Yard Gully", "polypipeStormYardGully", "Nr", "Polypipe-Storm", sumField("polypipeStormYardGully") || sumField("yardGully")),
      buildItemRow("2.4", "Drainage Channel", "polypipeStormDrainageChannel", "m", "Polypipe-Storm", sumField("polypipeStormDrainageChannel") || sumField("drainageChannel")),
      buildItemRow("2.5", "Land Drain", "polypipeStormLandDrain", "m", "Polypipe-Storm", sumField("polypipeStormLandDrain") || sumField("landDrain")),
    ];
  } else {
    const totalPlots = parseFloat(osmaConfig.foulTotalPlots) || 0;
    const nrPlotsTakeOff = parseFloat(osmaConfig.foulNrPlotsTakeOff) || 0;
    return [
      buildPerPlotRow("1.0", "Foul – OSMA", "osma32mmBend", OSMA_FOUL_COMBINED_FIELDS, "OSMA-Foul", totalPlots, nrPlotsTakeOff),
      buildItemRow("1.1", "NME PPIC", "osmaNmePpic", "Nr", "OSMA-Foul"),
      buildItemRow("1.2", "600mm NME PPIC", "osma600mmNmePpic", "Nr", "OSMA-Foul"),
      buildPerPlotRow("2.0", "Storm – OSMA", "osmaStorm32mmBend", OSMA_STORM_COMBINED_FIELDS, "OSMA-Storm", totalPlots, nrPlotsTakeOff),
      buildItemRow("2.1", "NME PPIC", "osmaStormNmePpic", "Nr", "OSMA-Storm"),
      buildItemRow("2.2", "600mm NME PPIC", "osmaStorm600mmNmePpic", "Nr", "OSMA-Storm"),
      buildItemRow("2.3", "Yard Gully", "osmaStormYardGully", "Nr", "OSMA-Storm", sumField("osmaStormYardGully") || sumField("yardGully")),
      buildItemRow("2.4", "Drainage Channel", "osmaStormDrainageChannel", "m", "OSMA-Storm", sumField("osmaStormDrainageChannel") || sumField("drainageChannel")),
      buildItemRow("2.5", "Land Drain", "osmaStormLandDrain", "m", "OSMA-Storm", sumField("osmaStormLandDrain") || sumField("landDrain")),
    ];
  }
}

export interface ExportDetailOptions {
  includeRates: boolean;
  includeQuantities: boolean;
  includeDescriptions: boolean;
  includeNotes: boolean;
  includeTotals: boolean;
  includeOhp: boolean;
  includePreliminaries: boolean;
  includeBreakdown: boolean;
  groupByCategory: boolean;
}

export interface BillSummaryData {
  items: Array<{
    itemNumber: string;
    description: string;
    quantity: number;
    unit: string;
    rate: number;
    amount: number;
    category?: string;
  }>;
  grandTotal: number;
}

/**
 * Main export function for all groundworks bill summaries
 */
export async function exportGroundworksBillSummariesToExcel(
  groundworksData: any,
  settings: any,
  projectInfo: {
    projectName: string;
    clientName: string;
    region: string;
    siteAddress?: string;
    locationData?: LocationData;
    creationDate?: Date;
  },
  exportOptions: ExportDetailOptions,
  includeDiscountDisplay: boolean = false,
  includePreliminariesDisplay: boolean = false,
  includeOhpDisplay: boolean = false,
  ratesMapping?: RateMappingJSON | null,
  itemLibraryData?: ItemLibraryResponse,
  persistedRetSpecs?: RetainingWallsBuildUpSpecification[],
  persistedGeoSpecs?: GeoToberWallBuildUpSpecification[],
) {
  try {
    // Debug logging
    console.log("📦 exportGroundworksBillSummariesToExcel called with itemLibraryData:", {
      hasData: !!itemLibraryData,
      isArray: Array.isArray(itemLibraryData),
      length: itemLibraryData?.length || 0,
    });

    // Build enriched groundworksData with persisted wall API specs injected into
    // wallsBuildUpDimensions / geoToberWallBuildUpDimensions so that
    // generateRetWallsCollectiveItems / generateGeoWallsCollectiveItems and
    // generatePlotMatrixCategoryRows all see real net values without requiring
    // the user to visit the wall tabs first.
    const enrichedGroundworksData = (() => {
      if (!groundworksData) return groundworksData;
      const merged = { ...groundworksData } as any;
      if (persistedRetSpecs && persistedRetSpecs.length > 0) {
        const dims: Record<string, Array<{ id: string; description: string; net: number; unit: string; rate?: number }>> = {};
        persistedRetSpecs.forEach((spec) => {
          dims[spec.id] = spec.items.map((item) => ({
            id: item.id,
            description: item.description,
            net: item.net,
            unit: item.unit,
            rate: item.rate,
          }));
        });
        merged.wallsBuildUpDimensions = dims;
      }
      if (persistedGeoSpecs && persistedGeoSpecs.length > 0) {
        const dims: Record<string, Array<{ id: string; description: string; net: number; unit: string; rate?: number }>> = {};
        persistedGeoSpecs.forEach((spec) => {
          dims[spec.id] = spec.items.map((item) => ({
            id: item.id,
            description: item.description,
            net: item.net,
            unit: item.unit,
            rate: item.rate,
          }));
        });
        merged.geoToberWallBuildUpDimensions = dims;
      }
      return merged;
    })();

    const ExcelJS = await import("exceljs");
    const workbook = new ExcelJS.default.Workbook();

    const primaryColor = "003366"; // App's primary color

    // Calculate tender total
    const grandTotal = calculateOverallGrandTotal(
      enrichedGroundworksData,
      settings,
      includePreliminariesDisplay,
      includeOhpDisplay,
      includeDiscountDisplay,
      ratesMapping,
      itemLibraryData,
      persistedRetSpecs,
      persistedGeoSpecs,
    );

    // Create cover sheet
    const coverSheetData: CoverSheetData = {
      projectName: projectInfo.projectName,
      clientName: projectInfo.clientName,
      region: projectInfo.region,
      jobType: "Groundworks",
      tenderTotal: grandTotal,
      dateCreated: new Date(),
      description: `Comprehensive groundworks bill of quantities for ${projectInfo.projectName}. This document includes all groundworks sections with detailed breakdowns and calculations.`,
      siteAddress: projectInfo.siteAddress,
      locationData: projectInfo.locationData,
    };

    createCoverSheet(workbook, coverSheetData);

    // Add logo to workbook
    const logoImageId = workbook.addImage({
      base64: MNJLOGOBASE64,
      extension: "jpeg",
    });

    // Define all bill summary sections (order matches UI Bill of Quantities Summary tabs)
    const sections = [
      { name: "Sum Analysis", stepName: "Sum Analysis", hasCategories: false },
      { name: "Cut & Fill", stepName: "Cut & Fills", hasCategories: true },
      { name: "Foundations", stepName: "Foundations", hasCategories: true },
      { name: "Drainage", stepName: "Drainage", hasCategories: true },
      { name: "Plot Drives", stepName: "Plot Drives", hasCategories: true },
      { name: "Plot Service", stepName: "Plot Service", hasCategories: true },
      { name: "Plot Paving", stepName: "Plot Pavings", hasCategories: true },
      { name: "Scaffold Platforms", stepName: "Scaffold", hasCategories: true },
      { name: "Garden", stepName: "Garden", hasCategories: true },
      { name: "Ret & Screen Walls", stepName: "Ret & Screen Walls", hasCategories: true },
      { name: "Geowall & Tobermore Walls", stepName: "Geowall & Tobermore Walls", hasCategories: true },
    ];

    // Create worksheet for each section
    for (const section of sections) {
      if (section.stepName === "Sum Analysis") {
        await createSumAnalysisSheet(
          workbook,
          logoImageId,
          enrichedGroundworksData,
          settings,
          projectInfo,
          primaryColor,
          exportOptions,
          includeDiscountDisplay,
          includePreliminariesDisplay,
          includeOhpDisplay,
          ratesMapping,
          itemLibraryData,
          persistedRetSpecs,
          persistedGeoSpecs,
        );

        // Create Notes sheet immediately after Sum Analysis
        console.log("📝 Creating Notes sheet...");
        await createNotesSheet(
          workbook,
          logoImageId,
          projectInfo,
          primaryColor,
        );
      } else {
        await createBillSummarySheet(
          workbook,
          logoImageId,
          section,
          enrichedGroundworksData,
          settings,
          projectInfo,
          primaryColor,
          exportOptions,
          ratesMapping,
          itemLibraryData,
          includePreliminariesDisplay,
          includeOhpDisplay,
        );
      }
    }

    // Dynamic Bill Summary tabs (the "+ Add Tab" bill summaries on
    // /groundworks/review, see useDynamicBillSummary.tsx). Exported
    // unconditionally, matching the on-screen tab bar — existing tabs stay
    // fully visible there even when the "Configure BOQ" switch is off (that
    // switch only gates add/rename/delete, not visibility), so there is no
    // equivalent gate to check here. Markup uses the same unconditional
    // formula the review page threads into DynamicBillSummaryPanel via
    // `dynamicBillSummaryMarkupFactor`, not the includePreliminariesDisplay/
    // includeOhpDisplay-gated formula the fixed sheets use, so the exported
    // amounts match what the on-screen dynamic tabs actually show.
    const dynamicTabs: DynamicBillSummaryTab[] = enrichedGroundworksData?.dynamicBillSummaryTabs ?? [];
    if (dynamicTabs.length > 0) {
      console.log(`📑 Creating ${dynamicTabs.length} Dynamic Bill Summary sheet(s)...`);
      const dynamicMarkupFactor =
        (1 + (settings?.groundworks?.overheadAndProfit ?? 0) / 100) *
        (1 + (settings?.groundworks?.preliminaries ?? 0) / 100);
      const usedSheetNames = new Set<string>(
        workbook.worksheets.map((ws: any) => String(ws.name).toLowerCase()),
      );
      for (const tab of dynamicTabs) {
        const sheetName = sanitizeWorksheetName(tab.label, usedSheetNames);
        await createDynamicBillSummarySheet(
          workbook,
          logoImageId,
          tab,
          projectInfo,
          primaryColor,
          exportOptions,
          dynamicMarkupFactor,
          sheetName,
        );
      }
    }

    // Create Plot Matrix sheet. Resolve markup percentages from the user's
    // include flags + the global settings so the BoQ export's Plot Matrix
    // sheet matches the on-screen Plot Matrix UI's tender total to the
    // penny. A disabled markup is passed as 0 so `createPlotMatrixSheet`
    // can stay agnostic of the toggle.
    const plotMatrixMarkup: PlotMatrixMarkup = {
      prelimsPct: includePreliminariesDisplay
        ? settings?.groundworks?.preliminaries ?? 0
        : 0,
      ohpPct: includeOhpDisplay
        ? settings?.groundworks?.overheadAndProfit ?? 0
        : 0,
      discountPct: includeDiscountDisplay
        ? settings?.groundworks?.discount ?? 0
        : 0,
    };
    console.log("📊 Creating Plot Matrix sheet...", plotMatrixMarkup);
    await createPlotMatrixSheet(
      workbook,
      logoImageId,
      enrichedGroundworksData,
      projectInfo,
      primaryColor,
      ratesMapping,
      itemLibraryData,
      undefined,
      undefined,
      'plot',
      settings,
      plotMatrixMarkup,
      persistedRetSpecs,
      persistedGeoSpecs,
    );

    // Create End Cover sheet
    const ENABLE_END_COVER = true; // Set to false to disable end cover temporarily
    const USE_SIMPLE_VERSION = false; // Now using full version with logos!

    if (ENABLE_END_COVER) {
      console.log("📄 Creating End Cover sheet...");
      try {
        if (USE_SIMPLE_VERSION) {
          console.log("📄 Using simplified end cover (no images)...");
          createEndCoverSimple(workbook as any, coverSheetData);
        } else {
          console.log("📄 Using full end cover with logos and exact design...");
          await createEndCover(workbook as any, coverSheetData);
        }
        console.log("✅ End Cover sheet created successfully");
      } catch (error) {
        console.error("❌ Failed to create End Cover sheet:", error);
        console.error(
          "Error details:",
          error instanceof Error ? error.message : String(error),
        );
        console.error(
          "Stack:",
          error instanceof Error ? error.stack : "No stack trace",
        );
        console.log("📄 Continuing export without End Cover...");
      }
    } else {
      console.log("⚠️ End Cover is disabled");
    }

    // Generate and download file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    const date = new Date().toISOString().split("T")[0];
    link.download = `Groundworks_BOQ_${projectInfo.projectName}_${date}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);

    return true;
  } catch (error) {
    console.error("Export error:", error);
    throw error;
  }
}

/**
 * Create Sum Analysis worksheet
 */
async function createSumAnalysisSheet(
  workbook: any,
  logoImageId: any,
  groundworksData: any,
  settings: any,
  projectInfo: any,
  primaryColor: string,
  exportOptions: ExportDetailOptions,
  includeDiscountDisplay: boolean = false,
  includePreliminariesDisplay: boolean = false,
  includeOhpDisplay: boolean = false,
  ratesMapping?: RateMappingJSON | null,
  itemLibraryData?: ItemLibraryResponse,
  persistedRetSpecs?: RetainingWallsBuildUpSpecification[],
  persistedGeoSpecs?: GeoToberWallBuildUpSpecification[],
) {
  const worksheet = workbook.addWorksheet("Sum Analysis");

  // Create header - Sum Analysis has 2 columns (Description, Amount)
  const headerRowNum = createWorksheetHeader(
    worksheet,
    logoImageId,
    "Groundworks Summary",
    projectInfo.projectName,
    projectInfo.clientName,
    projectInfo.region,
    primaryColor,
    2, // Number of columns
    projectInfo.creationDate,
    2, // logoMergeColumns = B column
  );

  // Column headers
  const headers = ["Description", "Amount"];
  styleColumnHeaders(worksheet, headers, headerRowNum, primaryColor);

  let currentRow = headerRowNum + 1;

  // Delegate section totals to the same shared calculator SumAnalysis.tsx
  // uses on screen (sumAnalysisGrandTotalCalculator.ts) instead of
  // reimplementing the per-section switch here. That shared calculator
  // already folds each domain's ad-hoc extra items (foundationsExtraItems,
  // cutFillsExtraItems, drainageExtraItems, etc — added via "Add Section"/
  // "Add Item" on a FIXED Bill Summary, not a Dynamic Bill Summary tab) into
  // that domain's own section total, and appends every Dynamic Bill Summary
  // tab as its own section — so this sheet (and the cover-sheet grand total,
  // see calculateOverallGrandTotal below) can't silently drop either kind of
  // user-added row the way the old duplicated switch statement did.
  const sumAnalysisResult = calculateSumAnalysisGrandTotal({
    groundworksData,
    ratesMapping,
    itemLibraryData,
    preliminariesPercent: settings?.groundworks?.preliminaries || 0,
    ohpPercent: settings?.groundworks?.overheadAndProfit || 0,
    discountPercent: settings?.groundworks?.discount || 0,
    includePreliminaries: includePreliminariesDisplay,
    includeOhp: includeOhpDisplay,
    includeDiscount: includeDiscountDisplay,
    enableLogging: false,
    retWallsBuildUpSpecs: persistedRetSpecs,
    geoToberWallBuildUpSpecs: persistedGeoSpecs,
  });

  sumAnalysisResult.sections.forEach((section) => {
    if (section.totalAmount === 0) return;

    worksheet.getCell(currentRow, 1).value = section.sectionName;
    worksheet.getCell(currentRow, 2).value = `£${section.totalAmount.toLocaleString("en-GB", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
    styleDataRow(worksheet, currentRow, 2);
    // Right align the amount column
    worksheet.getCell(currentRow, 2).alignment = {
      vertical: "middle",
      horizontal: "right",
    };
    currentRow++;
  });

  const subtotal = sumAnalysisResult.grandTotalWithAdjustments;
  let adjustedTotal = subtotal;

  // Add subtotal only if discount is enabled and there is a discount value
  if (includeDiscountDisplay && settings.groundworks.discount > 0) {
    currentRow++;
    worksheet.getCell(currentRow, 1).value = "SUB-TOTAL";
    worksheet.getCell(currentRow, 2).value = `£${subtotal.toLocaleString("en-GB", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
    styleTotalRow(worksheet, currentRow, 2);

    // Set alignment AFTER styling to ensure it's not overridden
    worksheet.getCell(currentRow, 1).alignment = {
      horizontal: "left",
      vertical: "middle",
    };

    currentRow++;

    // Add discount
    const discountAmount =
      adjustedTotal * (settings.groundworks.discount / 100);
    worksheet.getCell(currentRow, 1).value =
      `DISCOUNT (${settings.groundworks.discount}%)`;
    worksheet.getCell(currentRow, 2).value = `-£${discountAmount.toLocaleString("en-GB", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
    styleDataRow(worksheet, currentRow, 2);
    // Right align the amount column
    worksheet.getCell(currentRow, 2).alignment = {
      vertical: "middle",
      horizontal: "right",
    };
    currentRow++;
    adjustedTotal -= discountAmount;
  }

  // Add grand total
  currentRow++;
  worksheet.getCell(currentRow, 1).value = "TENDER TOTAL";
  worksheet.getCell(currentRow, 2).value = `£${adjustedTotal.toLocaleString("en-GB", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
  styleTotalRow(worksheet, currentRow, 2, true);

  // For Sum Analysis, no merging needed as we only have 2 columns
  // Ensure left alignment for the label
  worksheet.getCell(currentRow, 1).alignment = {
    horizontal: "left",
    vertical: "middle",
  };

  // Auto-fit column widths; stretch to fill A4 portrait print area
  autoFitColumns(worksheet, { columnMaxWidths: { 1: 90, 2: 30 }, defaultMin: 20, padding: 3, minTotalWidth: 100 });

  // Set row heights for better readability (apply to data rows only, skip header rows 1-8)
  worksheet.eachRow((row) => {
    if (row.number > 8) {
      // Skip logo and header rows (1-8)
      row.height = 22; // Increased height for all data rows for better visibility
    }
  });

  // Enable text wrapping for data cells only (skip header rows 1-8 and merged cells)
  worksheet.eachRow((row) => {
    if (row.number <= 8) {
      // Skip header rows - they have their own alignment set
      return;
    }
    row.eachCell((cell) => {
      // Skip cells that are part of merged ranges
      if (cell.isMerged) {
        return;
      }

      cell.alignment = {
        ...cell.alignment,
        wrapText: true,
        vertical: "middle",
        shrinkToFit: false,
      };
      // Ensure font is set to enable proper wrapping
      if (!cell.font || !cell.font.size) {
        cell.font = { ...cell.font, size: 11 }; // Standard font size
      }

      // Calculate and set row height based on actual cell content
      if (cell.value && typeof cell.value === "string" && cell.col === 1) {
        const lineCount = (cell.value.match(/\n/g) || []).length + 1;
        const charsPerLine = 65; // Column A width is 70 units
        const estimatedWrappedLines = Math.ceil(
          cell.value.length / charsPerLine,
        );
        const totalLines = Math.max(lineCount, estimatedWrappedLines);
        const extraPadding = 6; // Small padding for safety
        const calculatedHeight = Math.max(26, totalLines * 16 + extraPadding);
        if (row.height < calculatedHeight) {
          row.height = calculatedHeight;
        }
      }
    });
  });

  // Set page setup for printing - fit all columns to one page width
  worksheet.pageSetup = {
    paperSize: 9, // A4
    orientation: "portrait",
    fitToPage: true, // Enable fit to page to ensure columns don't split
    fitToWidth: 1, // All columns must fit on one page width
    fitToHeight: 0, // Allow multiple pages vertically
    margins: {
      left: 0.5,
      right: 0.5,
      top: 0.5,
      bottom: 0.5,
      header: 0.3,
      footer: 0.3,
    },
    horizontalCentered: true,
  };
}

/**
 * Create bill summary worksheet for a section
 */
/**
 * Append Section 3.0 Soil Management items (3.1 Topsoil, 3.2 Subsoil) to Cut & Fill BOQ items.
 * Mirrors GroundworksCutFillBillSummary.tsx Section 3.0 logic exactly.
 */
function appendSoilManagementItems(
  boqItems: any[],
  groundworksData: any,
  itemLibraryData?: ItemLibraryResponse,
): any[] {
  const topsoilMgmt = groundworksData?.topsoilManagement;
  const subsoilMgmt = groundworksData?.subsoilManagement;
  const soilData = groundworksData?.soilManagementData;

  // Build rate lookup from item library (flat array or object with .items)
  const libItems: any[] = Array.isArray(itemLibraryData)
    ? itemLibraryData
    : (itemLibraryData as any)?.items ?? [];
  const codeRateLookup = new Map<string, { rate: number; unit: string }>();
  libItems.forEach((item: any) => {
    const code = item.resourceCode?.trim().toUpperCase();
    if (code) {
      codeRateLookup.set(code, {
        rate: item.nettRate ?? 0,
        unit: item.unit ?? 'm³',
      });
    }
  });

  // --- 3.1 Topsoil ---
  const cutFillsTable: any[] = groundworksData?.cutFillsTable || [];
  const dataRows = cutFillsTable.filter(
    (r: any) => r.key !== 'totals' && r.plotNo !== 'TOTALS' && r.plotNo !== 'Total',
  );
  const totalOnsite = dataRows.reduce((s: number, r: any) => s + (Number(r.topsoilDisposalOnsite) || 0), 0);
  const totalOffsite = dataRows.reduce((s: number, r: any) => s + (Number(r.topsoilDisposalOffsite) || 0), 0);

  // Use user-edited soilManagementData topsoil values when present (same logic as BoQ on-screen)
  const soilTopsoilOnsite = soilData?.topsoilOnsite !== undefined ? Number(soilData.topsoilOnsite) : totalOnsite;
  const soilTopsoilOffsite = soilData?.topsoilOffsite !== undefined ? Number(soilData.topsoilOffsite) : totalOffsite;
  const topsoilQty = Math.abs(soilTopsoilOnsite - soilTopsoilOffsite);

  const topsoilCode = topsoilMgmt?.code?.trim().toUpperCase() || '';
  const topsoilIsExcluded = topsoilCode === 'NONE' || !topsoilCode;
  const topsoilLib = topsoilCode && topsoilCode !== 'NONE' ? codeRateLookup.get(topsoilCode) : null;
  const topsoilRate = topsoilLib?.rate ?? 0;
  const topsoilUnit = topsoilLib?.unit ?? 'm³';
  const topsoilDesc = topsoilIsExcluded
    ? (topsoilCode === 'NONE' ? 'Excluded' : 'Not Configured')
    : (topsoilMgmt?.subOption || topsoilCode);
  const topsoilAmount = topsoilIsExcluded ? 0 : topsoilQty * topsoilRate;

  // --- 3.2 Subsoil ---
  const soilRows = [
    { cutField: 'foundationsCutArisings', fillField: 'foundationsFill' },
    { cutField: 'slabPathsCut', fillField: 'slabPathsFill' },
    { cutField: 'gardensCut', fillField: 'gardensFill' },
    { cutField: 'drivesCut', fillField: 'drivesFill' },
    { cutField: 'foundationsArisingsCut', fillField: 'foundationsArisingsFill' },
    { cutField: 'plotDrainageCut', fillField: 'plotDrainageFill' },
    { cutField: 'wallsCut', fillField: 'wallsFill' },
    { cutField: 'plotServicesCut', fillField: 'plotServicesFill' },
  ];
  const subsoilTotalCut = soilRows.reduce((s, r) => s + (Number(soilData?.[r.cutField]) || 0), 0);
  const subsoilTotalFill = soilRows.reduce((s, r) => s + (Number(soilData?.[r.fillField]) || 0), 0);
  const subsoilQty = Math.abs(subsoilTotalCut - subsoilTotalFill);

  const subsoilCode = subsoilMgmt?.code?.trim().toUpperCase() || '';
  const subsoilIsExcluded = subsoilCode === 'NONE' || !subsoilCode;
  const subsoilLib = subsoilCode && subsoilCode !== 'NONE' ? codeRateLookup.get(subsoilCode) : null;
  const subsoilRate = subsoilLib?.rate ?? 0;
  const subsoilUnit = subsoilLib?.unit ?? 'm³';
  const subsoilDescBase = subsoilMgmt?.subOption || '';
  const subsoilDisposalClass = subsoilMgmt?.disposalClassification || '';
  const subsoilDescLabel = subsoilDescBase && subsoilDisposalClass
    ? `${subsoilDescBase} – ${subsoilDisposalClass}`
    : subsoilDescBase;
  const subsoilDesc = subsoilIsExcluded
    ? (subsoilCode === 'NONE' ? 'Excluded' : 'Not Configured')
    : (subsoilDescLabel || subsoilCode);
  const subsoilAmount = subsoilIsExcluded ? 0 : subsoilQty * subsoilRate;

  // Only push the actual line items — the hierarchical renderer creates subsection
  // headers automatically from the category name ("Soil Management - Topsoil Management").
  // Adding a separate header item would cause a duplicate "Topsoil Management" row.
  return [
    ...boqItems,
    {
      key: 'soil-topsoil-line',
      fieldName: 'soil-topsoil-line',
      category: 'Soil Management - Topsoil Management',
      description: topsoilDesc,
      quantity: topsoilIsExcluded ? 0 : Math.round(topsoilQty),
      unit: topsoilIsExcluded ? '' : topsoilUnit,
      rate: topsoilIsExcluded ? 0 : topsoilRate,
      amount: topsoilAmount,
    },
    {
      key: 'soil-subsoil-line',
      fieldName: 'soil-subsoil-line',
      category: 'Soil Management - Subsoil Management',
      description: subsoilDesc,
      quantity: subsoilIsExcluded ? 0 : Math.round(subsoilQty),
      unit: subsoilIsExcluded ? '' : subsoilUnit,
      rate: subsoilIsExcluded ? 0 : subsoilRate,
      amount: subsoilAmount,
    },
  ];
}

async function createBillSummarySheet(
  workbook: any,
  logoImageId: any,
  section: any,
  groundworksData: any,
  settings: any,
  projectInfo: any,
  primaryColor: string,
  exportOptions: ExportDetailOptions,
  ratesMapping?: RateMappingJSON | null,
  itemLibraryData?: ItemLibraryResponse,
  includePreliminariesDisplay: boolean = false,
  includeOhpDisplay: boolean = false,
) {
  const worksheet = workbook.addWorksheet(section.name);

  // Get BOQ data for this section
  // Override categories for Plot Service to match UI's full field list (PlotServiceBillSummary.tsx)
  const PLOT_SERVICE_FULL_CATEGORIES: Record<string, string[]> = {
    'Plot Services': [
      'plots', 'apartments', 'eo25mmBarrierPipe', 'eo32mmBarrierPipe',
      'virginMedia', 'garageDuct', 'privateStLightDuct', 'pccEdgingsGasBoxes',
      'ductingToChargingPoint', 'concBaseChargingPoints', 'installBollardBase', 'airSourceHeatPump',
    ],
    'Service Connections': [
      'serviceConnectionPlots', 'serviceConnectionApartments', 'vacEx',
    ],
  };

  const categories = section.stepName === "Plot Service"
    ? PLOT_SERVICE_FULL_CATEGORIES
    : (STEP_CATEGORIES as any)[section.stepName];

  // Sections that use custom generation bypass the category lookup
  const isCustomSection =
    section.stepName === "Ret & Screen Walls" ||
    section.stepName === "Geowall & Tobermore Walls" ||
    section.stepName === "Drainage";

  if (!categories && !isCustomSection) {
    console.log(`❌ No categories found for ${section.stepName}`);
    return;
  }

  if (categories) {
    console.log(`📊 Generating BOQ for ${section.stepName}:`, {
      categoriesCount: Object.keys(categories).length,
      categoryNames: Object.keys(categories),
    });
  }

  // Get dimension values for sections that need them (same as GenericBillSummary)
  const dimensionValues =
    section.stepName === "Plot Drives"
      ? (groundworksData as any)?.plotDrivesDimensionValues
      : section.stepName === "Plot Pavings"
        ? (groundworksData as any)?.plotPavingsDimensionValues
        : section.stepName === "Scaffold"
          ? (groundworksData as any)?.scaffoldDimensionValues
          : section.stepName === "Garden"
            ? (groundworksData as any)?.gardenDimensionValues
            : undefined;

  // Get column headers for dynamic label display (same as GenericBillSummary)
  const columnHeaders =
    section.stepName === "Plot Drives"
      ? reconstructPlotDrivesColumnHeaders(
          (groundworksData as any)?.plotDrivesColumnHeaders || {},
          (groundworksData as any)?.plotDrivesDimensionValues || {}
        )
      : section.stepName === "Plot Pavings"
        ? reconstructPlotPavingsColumnHeaders(
            (groundworksData as any)?.plotPavingsColumnHeaders || {},
            (groundworksData as any)?.plotPavingsDimensionValues || {}
          )
        : section.stepName === "Scaffold"
          ? (groundworksData as any)?.scaffoldColumnHeaders
          : section.stepName === "Garden"
            ? (groundworksData as any)?.gardenColumnHeaders
            : undefined;

  console.log(`🔑 Dimension values for ${section.stepName}:`, {
    hasDimensionValues: !!dimensionValues,
    keys: dimensionValues ? Object.keys(dimensionValues) : [],
  });

  let boqItems: any[];
  try {
    // Special handling for sections that use their own generation logic
    if (section.stepName === "Ret & Screen Walls") {
      boqItems = generateRetWallsCollectiveItems(groundworksData, itemLibraryData, settings);
      console.log(`🏗️ Generated ${boqItems.length} collective items for Retaining Walls`);
    } else if (section.stepName === "Geowall & Tobermore Walls") {
      boqItems = generateGeoWallsCollectiveItems(groundworksData, itemLibraryData, settings);
      console.log(`🏗️ Generated ${boqItems.length} collective items for Geowall & Tobermore Walls`);
    } else if (section.stepName === "Drainage") {
      boqItems = generateDrainageBillSummaryItems(groundworksData, itemLibraryData);
      console.log(`🏗️ Generated ${boqItems.length} drainage items`);
    } else {
      boqItems = generateBOQSummaryWithCategories(
        groundworksData,
        section.stepName,
        categories,
        dimensionValues,
        ratesMapping,
        columnHeaders,
      );
    }

    // Hoisted so the Scaffold consolidation block (which runs after the try/catch)
    // can access the rate map for per-component fallback lookups.
    let rateLookupMap: Map<string, any> = new Map();

    // Apply item library rates if available
    if (itemLibraryData && Array.isArray(itemLibraryData) && itemLibraryData.length > 0 && boqItems.length > 0) {
      try {
        console.log(`🔄 Applying item library rates for ${section.stepName}...`);

        switch (section.stepName) {
          case "Foundations":
            rateLookupMap = createFoundationsRateLookupMap(itemLibraryData, groundworksData);
            boqItems = applyFoundationsRatesToSummary(boqItems, rateLookupMap);
            break;
          case "Cut & Fills": {
            rateLookupMap = createCutFillsRateLookupMap(itemLibraryData);
            boqItems = applyCutFillsRatesToSummary(boqItems, rateLookupMap);
            // Apply proper descriptions matching CutFills.tsx column titles
            const cutFillPrefixValues = (groundworksData as any)?.cutFillsPrefixValues as Record<string, string> | undefined ?? {};
            boqItems = boqItems.map((item: any) => {
              let description = CUT_FILL_FIELD_DESCRIPTIONS[item.fieldName] ?? item.description;
              // Decimal entries (e.g. "0.150") are converted to mm for display
              // via `formatDimensionLabel` so the export matches the bill summary.
              if (item.fieldName === 'topsoilStrip' && cutFillPrefixValues['topsoilStrip']) {
                description = description.replace('xx', formatDimensionLabel(cutFillPrefixValues['topsoilStrip']));
              }
              return { ...item, description };
            });
            // Remap Cut & Fill items to sub-sections by column type, matching UI hierarchy
            // (Site Clearance items remain flat; Cut & Fill items become "Cut & Fill - Founds" etc.)
            boqItems = boqItems.map((item: any) => {
              if (item.category !== 'Cut & Fill') return item;
              return { ...item, category: `Cut & Fill - ${getCutFillColumnType(item.fieldName)}` };
            });
            // Append Section 3.0 Soil Management rows (Topsoil + Subsoil)
            boqItems = appendSoilManagementItems(boqItems, groundworksData, itemLibraryData);
            break;
          }
          case "Drainage":
            // Rates already applied by generateDrainageBillSummaryItems
            break;
          case "Plot Drives":
            rateLookupMap = createPlotDrivesRateLookupMap(itemLibraryData, groundworksData);
            boqItems = applyPlotDrivesRatesToSummary(boqItems, rateLookupMap);
            break;
          case "Plot Service": {
            rateLookupMap = createPlotServiceRateLookupMap(itemLibraryData);
            boqItems = applyPlotServiceRatesToSummary(boqItems, rateLookupMap);
            // Apply apartments multipliers to match PlotServiceBillSummary.tsx
            const plotServiceConfig = (groundworksData as any)?.plotServiceConfig as {
              plotServicesApartmentsMultiple?: number;
              serviceConnectionApartmentsMultiple?: number;
            } | undefined;
            if (plotServiceConfig) {
              boqItems = boqItems.map((item: any) => {
                if (item.fieldName === 'apartments') {
                  const multiple = plotServiceConfig.plotServicesApartmentsMultiple ?? 1;
                  // Mirror PlotServiceBillSummary.tsx: pre-round base rate BEFORE multiplying
                  const baseRate = parseFloat(item.rate.toFixed(2));
                  if (multiple > 1) {
                    const effectiveRate = parseFloat((baseRate * multiple).toFixed(2));
                    return { ...item, rate: effectiveRate };
                  }
                  return { ...item, rate: baseRate };
                } else if (item.fieldName === 'serviceConnectionApartments') {
                  const multiple = plotServiceConfig.serviceConnectionApartmentsMultiple ?? 1;
                  const baseRate = parseFloat(item.rate.toFixed(2));
                  if (multiple > 1) {
                    const effectiveRate = parseFloat((baseRate * multiple).toFixed(2));
                    return { ...item, rate: effectiveRate };
                  }
                  return { ...item, rate: baseRate };
                }
                return item;
              });
            }
            break;
          }
          case "Plot Pavings":
            rateLookupMap = createPlotPavingsRateLookupMap(itemLibraryData, groundworksData);
            boqItems = applyPlotPavingsRatesToSummary(boqItems, rateLookupMap);
            break;
          case "Scaffold":
            rateLookupMap = createScaffoldRateLookupMap(itemLibraryData, groundworksData);
            boqItems = applyScaffoldRatesToSummary(boqItems, rateLookupMap);
            break;
          case "Garden":
            rateLookupMap = createGardenRateLookupMap(itemLibraryData, groundworksData);
            boqItems = applyGardenRatesToSummary(boqItems, rateLookupMap);
            break;
          case "Ret & Screen Walls":
            // Rates already applied by generateRetWallsCollectiveItems
            break;
          case "Geowall & Tobermore Walls":
            // Rates already applied by generateGeoWallsCollectiveItems
            break;
        }
        console.log(`✅ Item library rates applied for ${section.stepName}`);
      } catch (rateError) {
        console.error(`⚠️ Error applying item library rates for ${section.stepName}:`, rateError);
        // Continue with BOQ items as-is if rate application fails
      }
    } else {
      console.log(`⚠️ Skipping item library rates for ${section.stepName} - itemLibraryData not available`);
    }

    // Apply Scaffold consolidation to match UI (ScaffoldBillSummary.tsx)
    // Merges Level & Compact, Geotextile Layer, Crush 6F2, Type1 into one line per category.
    // Rate = sum of the Level & Compact / Crush 6F2 / Type1 rates, plus the
    // Geotextile Layer rate ONLY when that section's checkbox
    // (scaffoldDimensionValues[geotextileEnabledKey] === "true") is on.
    if (section.stepName === "Scaffold") {
      const CONSOLIDATE_FIELDS: Record<string, { quantityField: string; rateFields: string[]; crushField: string; type1Field: string; geotextileField: string; geotextileEnabledKey: string }> = {
        "Scaffold Platforms": {
          quantityField: "scaffoldPlatform",
          rateFields: ["scaffoldLevelCompact", "scaffoldGeotextileLayer", "scaffoldCrush6F2", "scaffoldType1"],
          crushField: "scaffoldCrush6F2",
          type1Field: "scaffoldType1",
          geotextileField: "scaffoldGeotextileLayer",
          geotextileEnabledKey: "scaffoldGeotextileEnabled",
        },
        "Loading Bays": {
          quantityField: "loadingBay4x2m",
          rateFields: ["loadingLevelCompact", "loadingGeotextileLayer", "loadingCrush6F2", "loadingType1"],
          crushField: "loadingCrush6F2",
          type1Field: "loadingType1",
          geotextileField: "loadingGeotextileLayer",
          geotextileEnabledKey: "loadingGeotextileEnabled",
        },
        "Stair Access": {
          quantityField: "stairAccess3x3m",
          rateFields: ["stairLevelCompact", "stairGeotextileLayer", "stairCrush6F2", "stairType1"],
          crushField: "stairCrush6F2",
          type1Field: "stairType1",
          geotextileField: "stairGeotextileLayer",
          geotextileEnabledKey: "stairGeotextileEnabled",
        },
      };

      const scaffoldDimensionValues: Record<string, string> = (groundworksData as any)?.scaffoldDimensionValues || {};
      const consolidated: any[] = [];
      const processedCategories = new Set<string>();

      boqItems.forEach((item: any) => {
        const config = CONSOLIDATE_FIELDS[item.category];
        if (!config) {
          consolidated.push(item);
          return;
        }

        // Skip individual rate fields — they'll be merged into the consolidated line
        if (config.rateFields.includes(item.fieldName)) {
          return;
        }

        // Use the base quantity field to create the consolidated line item
        if (item.fieldName === config.quantityField) {
          if (!processedCategories.has(item.category)) {
            processedCategories.add(item.category);

            const platformQuantity = item.quantity;

            // Mirror ScaffoldBillSummary.tsx: combinedRate = sum of the Level &
            // Compact / Crush 6F2 / Type1 rates, plus Geotextile Layer's rate
            // only when that section's checkbox is on. Raw (pre-markup) component
            // rates are kept on `_rawComponentRates` so the markup step below can
            // round each one AFTER applying markup, then sum — matching the Rate
            // Breakdown modal's Final Rate exactly (rather than rounding once
            // after summing raw rates).
            const geotextileEnabled = scaffoldDimensionValues[config.geotextileEnabledKey] === "true";
            const rawComponentRates: number[] = [];
            const combinedRate = config.rateFields.reduce((sum: number, fieldName: string) => {
              const rateItem = boqItems.find(
                (i: any) => i.fieldName === fieldName && i.category === item.category,
              );
              const fallbackRateInfo = rateLookupMap.get(fieldName);
              const rate = rateItem?.rate ?? fallbackRateInfo?.rate ?? 0;
              if (fieldName === config.geotextileField && !geotextileEnabled) return sum;
              rawComponentRates.push(rate);
              return sum + rate;
            }, 0);
            const totalAmount = combinedRate * platformQuantity;

            // Description: match ScaffoldBillSummary.tsx buildConsolidatedDescription —
            // formatDimensionLabel already returns integers (e.g. "150"), no "mm" suffix.
            const crushValue = scaffoldDimensionValues[config.crushField];
            const type1Value = scaffoldDimensionValues[config.type1Field];
            const crushDim = crushValue ? formatDimensionLabel(crushValue) : "xxx";
            const type1Dim = type1Value ? formatDimensionLabel(type1Value) : "xx";
            const description = `${crushDim} crushed concrete, blinded with ${type1Dim} thick Type 1${geotextileEnabled ? " including geotextile layer" : ""}`;

            consolidated.push({
              key: `consolidated-${item.category}`,
              fieldName: `consolidated-${item.category}`,
              description,
              quantity: platformQuantity,
              unit: "m²",
              rate: combinedRate,
              amount: totalAmount,
              category: item.category,
              _rawComponentRates: rawComponentRates,
            });
          }
          return;
        }

        // Any other fields in this category pass through as-is
        consolidated.push(item);
      });

      boqItems = consolidated;
    }

    // Remap Plot Drives categories to match UI hierarchy (PlotDrivesBillSummary.tsx)
    // UI groups by stage ("Drives to Binder" / "Drives Surface Course") then by drive type
    // The raw categories have "DriveType - Stage" format, swap to "Stage - DriveType"
    if (section.stepName === "Plot Drives") {
      const BINDER_SECTIONS = [
        'Private Macadam - Drives to Binder',
        'Private Block Paved - Drives to Binder',
        'Private Permeable - Drives to Binder',
        'Shared Macadam - Drives to Binder',
        'Shared Block Paved - Drives to Binder',
        'Shared Permeable - Drives to Binder',
        'Edgings & Kerbs',
      ];
      const SURFACE_SECTIONS = [
        'Private Macadam - Surface Course',
        'Private Block Paved - Surface Course',
        'Private Permeable - Surface Course',
        'Shared Macadam - Surface Course',
        'Shared Block Paved - Surface Course',
        'Shared Permeable - Surface Course',
        'Ancillary Items',
      ];

      boqItems = boqItems.map((item: any) => {
        const cat = item.category || "";
        if (BINDER_SECTIONS.includes(cat)) {
          // Remap to "Stage - DriveType" so the hierarchical renderer puts items under
          // the correct main section. Store the original full section name in
          // _fullSectionLabel so the sub-section header matches the UI exactly
          // (e.g. "PRIVATE MACADAM - DRIVES TO BINDER" not just "PRIVATE MACADAM").
          const parts = cat.split(" - ");
          if (parts.length === 2) {
            return { ...item, category: `Drives to Binder - ${parts[0]}`, _fullSectionLabel: cat };
          }
          return { ...item, category: `Drives to Binder - ${cat}`, _fullSectionLabel: cat };
        }
        if (SURFACE_SECTIONS.includes(cat)) {
          const parts = cat.split(" - ");
          if (parts.length === 2) {
            return { ...item, category: `Drives Surface Course - ${parts[0]}`, _fullSectionLabel: cat };
          }
          return { ...item, category: `Drives Surface Course - ${cat}`, _fullSectionLabel: cat };
        }
        return item;
      });
    }

    // Normalise every line and apply prelims+OH&P markup at the rate level —
    // same formula used by `createSumAnalysisSheet` and `calculateOverallGrandTotal`
    // so each bill summary tab's GRAND TOTAL matches the corresponding Sum Analysis line.
    const prelimsPercent = includePreliminariesDisplay ? (settings.groundworks.preliminaries || 0) : 0;
    const ohpPct = includeOhpDisplay ? (settings.groundworks.overheadAndProfit || 0) : 0;
    const markupFactor = (1 + ohpPct / 100) * (1 + prelimsPercent / 100);
    boqItems = boqItems.map((item: any) => {
      const roundedQty = Math.abs(Math.round(item.quantity || 0));
      const rawRate = (item.rate || 0) as number;
      let markedUpRate: number;
      if (section.stepName === 'Plot Service') {
        // PlotServiceBillSummary.tsx:
        //   baseRate = parseFloat(item.rate.toFixed(2))  ← pre-round base to 2dp
        //   markedUpRate = effectiveRate * markupFactor   ← no .toFixed(2) on rate
        //   amount = Math.abs(qty * markedUpRate).toFixed(2)  ← round the AMOUNT per row
        // Grand total = Σ parseFloat(item.amount) — sum of per-row 2dp amounts.
        // We must replicate the per-row amount rounding here so the export total
        // matches the on-screen total to the penny.
        const baseRate = parseFloat(rawRate.toFixed(2));
        markedUpRate = baseRate * markupFactor;
        const amount = parseFloat(Math.abs(roundedQty * markedUpRate).toFixed(2));
        return { ...item, quantity: roundedQty, rate: markedUpRate, amount };
      } else if (section.stepName === 'Scaffold' && Array.isArray(item._rawComponentRates)) {
        // Scaffold consolidated "crushed concrete / Type1" line: round each
        // component's marked-up rate to 2dp THEN sum — matches
        // ScaffoldBillSummary.tsx's Rate Breakdown modal Final Rate exactly.
        markedUpRate = (item._rawComponentRates as number[]).reduce(
          (sum: number, r: number) => sum + parseFloat((r * markupFactor).toFixed(2)),
          0,
        );
      } else {
        // Multiply raw rate by markupFactor BEFORE rounding — matches summaryCalculations.ts.
        markedUpRate = parseFloat((rawRate * markupFactor).toFixed(2));
      }
      return { ...item, quantity: roundedQty, rate: markedUpRate, amount: roundedQty * markedUpRate };
    });

    // Remove zero-quantity items before rendering so empty section headers are
    // never written — matches the UI bill summaries which filter these out.
    // (In the hierarchical path, section/sub-section headers are emitted
    // unconditionally, so without this filter they would appear even with no
    // data rows beneath them.)
    boqItems = boqItems.filter((item: any) => item.quantity !== 0);

    console.log(`✅ BOQ items generated for ${section.stepName}:`, {
      itemsCount: boqItems.length,
      sampleItem: boqItems[0] || "none",
      totalAmount: boqItems.reduce(
        (sum: number, item: any) => sum + item.amount,
        0,
      ),
    });
  } catch (error) {
    console.error(`❌ Error generating BOQ for ${section.stepName}:`, error);
    return;
  }

  if (!boqItems || boqItems.length === 0) {
    console.log(
      `⚠️ No BOQ items found for ${section.stepName} - skipping sheet`,
    );
    return;
  }

  // Build headers first to determine number of columns
  const effectiveExportOptions = exportOptions;
  const { headers, dataKeys } = buildHeaders(effectiveExportOptions);

  // Create header with correct number of columns
  const headerRowNum = createWorksheetHeader(
    worksheet,
    logoImageId,
    section.name,
    projectInfo.projectName,
    projectInfo.clientName,
    projectInfo.region,
    primaryColor,
    headers.length, // Pass the actual number of columns
  );

  styleColumnHeaders(worksheet, headers, headerRowNum, primaryColor);

  // Add data with category grouping
  let currentRow = headerRowNum + 1;
  let currentCategory = "";
  let categoryTotal = 0;

  // "Item" column numbering — one continuous serial count (1, 2, 3, 4...)
  // shared by EVERY row this sheet writes, real fixed rows and any
  // interleaved ad-hoc extra item alike, so an extra item inserted between
  // two real rows continues their sequence instead of restarting at 1.
  let sharedItemNumber = 1;

  // Ad-hoc "Add Section"/"Add Item" rows added directly to THIS fixed Bill
  // Summary (not a Dynamic Bill Summary tab) — state.groundworks.<domain>ExtraItems,
  // see useBillSummaryExtraItems.tsx and EXTRA_ITEMS_FIELD_BY_STEP_NAME above.
  // sumAnalysisGrandTotalCalculator.ts already folds these into this domain's
  // Sum Analysis row; this sheet renders them interleaved at the exact
  // position the on-screen table puts them — immediately after the real
  // rows of the category/subsection they're grafted onto, before that
  // category's own subtotal (via `flushMatchedExtras`, called from inside
  // both rendering branches below) — rather than as one separate block, so
  // the export reads like the UI. useBillSummaryExtraItems.tsx's buckets are
  // keyed by "attachment point" — `"__root__"` for a brand-new top-level
  // section/item (on screen, `buildRootExtraRows` gives each its own
  // numbered header, no wrapper), or an existing category row's own
  // on-screen `key` (`category-${name}` / `section-${name}` /
  // `col-type-${name}`, depending on which fixed Bill Summary component —
  // Foundations/Plot Service/Plot Pavings/Scaffold/Garden, PlotDrives, and
  // Cut & Fill respectively) for an item grafted onto that category. This
  // sheet's own `boqItems` share the same category names
  // (`generateBOQSummaryWithCategories` is the shared source both the
  // on-screen table and this sheet build from), so a bucket's attachKey is
  // matched back to one of those real categories. A bucket whose attachKey
  // matches no known category here (a differently-generated sheet —
  // Drainage/Ret & Screen Walls/Geowall & Tobermore Walls — or a row that's
  // since disappeared from the take-off) falls back to the same generic
  // "ADDITIONAL ITEMS" heading the on-screen table's own orphan fallback
  // uses (e.g. `orphanedExtraRows` in FoundationsBillSummary.tsx), rather
  // than being silently dropped.
  let extraItemsTotal = 0;
  const extraItemsPrelimsPercent = includePreliminariesDisplay ? (settings.groundworks.preliminaries || 0) : 0;
  const extraItemsOhpPercent = includeOhpDisplay ? (settings.groundworks.overheadAndProfit || 0) : 0;
  const extraItemsMarkupFactor = (1 + extraItemsOhpPercent / 100) * (1 + extraItemsPrelimsPercent / 100);
  const extraItemsFieldName = EXTRA_ITEMS_FIELD_BY_STEP_NAME[section.stepName];
  const extraItemsBuckets = extraItemsFieldName
    ? (groundworksData as Record<string, Record<string, DynamicBillSummaryNode[]> | undefined> | undefined)?.[extraItemsFieldName]
    : undefined;

  // Each fixed Bill Summary component's own row `key` scheme was audited
  // against this sheet's own `.category` strings (see the "Excel export
  // coverage" note in knowledge_graph.md's Dynamic Bill Summary section) —
  // most domains use a simple `<prefix>-<categoryName>` key with an exact
  // string match, but a few use fixed literal keys or reorder hierarchy
  // segments, needing the extra handling below.
  const knownCategoryNames = new Set<string>(); // segment-level: last AND first segment of every "A - B" category
  const knownFullCategories = new Set<string>(); // the full, unsplit category string
  boqItems.forEach((item: any) => {
    if (!item.category) return;
    const raw = String(item.category);
    knownFullCategories.add(raw);
    const parts = raw.split(" - ");
    knownCategoryNames.add(parts[parts.length - 1]);
    if (parts.length > 1) knownCategoryNames.add(parts[0]);
  });

  // Fixed literal attach-point keys with no derivable name (GroundworksCutFillBillSummary.tsx's
  // "site-clearance-parent"/"cut-fill-parent"/"soil-*-parent" headers,
  // RetScreenWallsBillSummary.tsx's "cat-screen-walls") mapped straight to
  // the real category/section name they correspond to on this sheet.
  const EXTRA_ATTACH_KEY_ALIASES: Partial<Record<string, Record<string, string>>> = {
    "Cut & Fills": {
      "site-clearance-parent": "Site Clearance",
      "cut-fill-parent": "Cut & Fill",
      "soil-management-parent": "Soil Management",
      "soil-topsoil-parent": "Topsoil Management",
      "soil-subsoil-parent": "Subsoil Management",
    },
    "Ret & Screen Walls": {
      "cat-screen-walls": "Screen Walls",
    },
    // PlotDrivesBillSummary.tsx's own two MAIN section headers ("DRIVES TO
    // BINDER"/"DRIVES SURFACE COURSE") are fixed literal keys, same
    // convention as Cut & Fill's "cut-fill-parent" — an item/section added
    // directly on one of those headers (rather than on a specific drive-type
    // subsection under it) used neither a `category-`/`section-`/`col-type-`
    // prefix nor any known name, so it fell straight into the orphaned
    // fallback. Aliased to the exact mainSectionName the hierarchical render
    // branch's own `flushMatchedExtras(mainSectionName)` call already looks
    // up by.
    "Plot Drives": {
      "main-binder": "Drives to Binder",
      "main-surface": "Drives Surface Course",
    },
  };
  const domainAttachKeyAliases = EXTRA_ATTACH_KEY_ALIASES[section.stepName] ?? {};

  const isKnownCategory = (name: string) => knownCategoryNames.has(name) || knownFullCategories.has(name);

  const matchExtraCategory = (attachKey: string): string | undefined => {
    const alias = domainAttachKeyAliases[attachKey];
    if (alias) return isKnownCategory(alias) ? alias : undefined;

    // DrainageBillSummary.tsx uses the same fixed "section-1"/"section-2"
    // keys regardless of OSMA vs Polypipe mode — resolve to whichever this
    // sheet's own live category actually is.
    if (section.stepName === "Drainage" && (attachKey === "section-1" || attachKey === "section-2")) {
      const marker = attachKey === "section-1" ? "foul" : "storm";
      return [...knownFullCategories].find((name) => name.toLowerCase().includes(marker));
    }

    for (const prefix of ["category-", "section-", "col-type-", "cat-"]) {
      if (!attachKey.startsWith(prefix)) continue;
      const suffix = attachKey.slice(prefix.length);
      if (isKnownCategory(suffix)) return suffix;
      // PlotDrivesBillSummary.tsx's own sectionName keeps "specific - general"
      // order (e.g. "Private Macadam - Drives to Binder"), while this sheet's
      // hierarchical categories go "general - specific"
      // ("Drives to Binder - Private Macadam").
      if (suffix.includes(" - ")) {
        const segments = suffix.split(" - ");
        // Prefer a full category whose own segments are exactly this same
        // SET (any order) — e.g. resolves to the sheet's own real
        // "Drives to Binder - Private Macadam" category, not the bare
        // "Private Macadam" segment. A bare-segment match is ambiguous
        // whenever the same subsection name repeats under more than one
        // main section (Plot Drives' "Private Macadam" appears under BOTH
        // "Drives to Binder" and "Drives Surface Course") — matching on the
        // bare segment alone would attach to whichever main section happens
        // to render first, silently misfiling anything meant for the other.
        const segmentSet = new Set(segments);
        const fullMatch = [...knownFullCategories].find((full) => {
          const fullSegments = full.split(" - ");
          return fullSegments.length === segments.length && fullSegments.every((s) => segmentSet.has(s));
        });
        if (fullMatch) return fullMatch;

        // No full category has this exact segment set — fall back to a
        // single matching segment (still ambiguous across main sections,
        // but better than dropping the item into the orphaned fallback).
        const found = segments.find((segment) => isKnownCategory(segment));
        if (found) return found;
      }
    }

    // No recognised prefix at all — still allow a bare name match.
    return isKnownCategory(attachKey) ? attachKey : undefined;
  };

  const rootExtraNodes: DynamicBillSummaryNode[] = [];
  const matchedExtrasByCategory = new Map<string, DynamicBillSummaryNode[]>();
  const orphanedExtraBuckets: Array<{ attachKey: string; nodes: DynamicBillSummaryNode[] }> = [];

  Object.entries(extraItemsBuckets ?? {}).forEach(([attachKey, nodes]) => {
    if (!nodes || nodes.length === 0) return;
    if (attachKey === "__root__") {
      rootExtraNodes.push(...nodes);
      return;
    }
    const matchedCategory = matchExtraCategory(attachKey);
    if (matchedCategory) {
      if (!matchedExtrasByCategory.has(matchedCategory)) matchedExtrasByCategory.set(matchedCategory, []);
      matchedExtrasByCategory.get(matchedCategory)!.push(...nodes);
    } else {
      orphanedExtraBuckets.push({ attachKey, nodes });
    }
  });

  const writeExtraNode = (node: DynamicBillSummaryNode, depth: number) => {
    if (node.type === "section") {
      try {
        worksheet.mergeCells(currentRow, 1, currentRow, headers.length);
      } catch (e) {
        console.error("Error merging Additional Items section cells:", e);
      }
      worksheet.getCell(currentRow, 1).value = `${"  ".repeat(depth)}${node.label.toUpperCase()}`;
      styleCategoryHeaderRow(worksheet, currentRow, headers.length, depth === 0);
      worksheet.getCell(currentRow, 1).alignment = { horizontal: "left", vertical: "middle" };
      currentRow++;
      node.children.forEach((child) => writeExtraNode(child, depth + 1));
      return;
    }

    const isText = node.amountType === "text";
    const amount = computeDynamicBillSummaryNodeAmount(node, extraItemsMarkupFactor);
    const rate = !isText && node.rate !== undefined ? computeDynamicBillSummaryNodeRate(node.rate, extraItemsMarkupFactor) : undefined;
    extraItemsTotal += amount;

    let colIndex = 1;
    dataKeys.forEach((key: string) => {
      let value: any = "";
      switch (key) {
        case "Item":
          value = `${sharedItemNumber}`;
          break;
        case "Description":
          value = `${"  ".repeat(depth)}${node.description || ""}`;
          break;
        case "Qty": {
          const qty = !isText && node.qty ? Math.round(node.qty) : 0;
          value = qty !== 0 ? qty : "";
          break;
        }
        case "Unit":
          value = isText ? "" : node.unit || "";
          break;
        case "Rate":
          value = rate ? `£${rate.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "";
          break;
        case "Amount":
          value = isText
            ? node.amountText || ""
            : amount !== 0
              ? `£${Math.abs(amount).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
              : "";
          break;
      }
      worksheet.getCell(currentRow, colIndex).value = value;
      colIndex++;
    });

    styleDataRow(worksheet, currentRow, headers.length);
    sharedItemNumber++;
    currentRow++;
  };

  const writeExtraGroupHeader = (label: string) => {
    try {
      worksheet.mergeCells(currentRow, 1, currentRow, headers.length);
    } catch (e) {
      console.error("Error merging Additional Items header cells:", e);
    }
    worksheet.getCell(currentRow, 1).value = label;
    styleCategoryHeaderRow(worksheet, currentRow, headers.length, true);
    worksheet.getCell(currentRow, 1).alignment = { horizontal: "left", vertical: "middle" };
    currentRow++;
  };

  /** Writes any extra rows grafted onto `categoryName` right where the
   *  caller currently is in the render loop — immediately after that
   *  category/subsection's own real rows, before its subtotal — and returns
   *  their combined amount so the caller can fold it into its own running
   *  subtotal. Removes the entry so the trailing sweep after both rendering
   *  branches only sees matched categories that never actually appeared
   *  while rendering (e.g. every fixed row in that category is currently
   *  zero-qty/hidden, so the category never got a header of its own here). */
  const flushMatchedExtras = (categoryName: string): number => {
    const nodes = matchedExtrasByCategory.get(categoryName);
    if (!nodes || nodes.length === 0) return 0;
    matchedExtrasByCategory.delete(categoryName);
    const before = extraItemsTotal;
    nodes.forEach((node) => writeExtraNode(node, 1));
    return extraItemsTotal - before;
  };

  // Check if categories are hierarchical (contain " - ")
  const hasHierarchy = boqItems.some(
    (item: any) => item.category && item.category.includes(" - "),
  );

  if (hasHierarchy) {
    // Group items by main section and subsection
    const mainSections = new Map<string, Map<string, any[]>>();

    boqItems.forEach((item: any) => {
      if (!item.category) return;
      const parts = item.category.split(" - ");
      const mainSection = parts[0] || item.category;
      const subSection = parts[1] || "";

      if (!mainSections.has(mainSection)) {
        mainSections.set(mainSection, new Map());
      }
      if (!mainSections.get(mainSection)!.has(subSection)) {
        mainSections.get(mainSection)!.set(subSection, []);
      }
      mainSections.get(mainSection)!.get(subSection)!.push(item);
    });

    mainSections.forEach((subSections, mainSectionName) => {
      // Add main section header - merge cells to span all columns
      try {
        worksheet.mergeCells(currentRow, 1, currentRow, headers.length);
      } catch (e) {
        console.error("Error merging main section header cells:", e);
      }

      worksheet.getCell(currentRow, 1).value = mainSectionName.toUpperCase();

      styleCategoryHeaderRow(worksheet, currentRow, headers.length, true);
      // Make main section header bold with darker background
      for (let i = 1; i <= headers.length; i++) {
        worksheet.getCell(currentRow, i).fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FF003366" },
        };
        worksheet.getCell(currentRow, i).font = {
          bold: true,
          color: { argb: "FFFFFFFF" },
        };
      }

      // Set alignment AFTER styling to ensure it's not overridden
      worksheet.getCell(currentRow, 1).alignment = {
        horizontal: "left",
        vertical: "middle",
      };

      currentRow++;

      let mainSectionTotal = 0;

      subSections.forEach((items, subSectionName) => {
        // Use the full original section label when available (e.g. Plot Drives stores
        // _fullSectionLabel so the header matches the UI: "PRIVATE MACADAM - DRIVES TO BINDER"
        // rather than just "PRIVATE MACADAM" which the split-on-" - " would give).
        const subSectionDisplayName = items[0]?._fullSectionLabel || subSectionName;

        if (subSectionName) {
          // Add subsection header - merge cells to span all columns
          try {
            worksheet.mergeCells(currentRow, 1, currentRow, headers.length);
          } catch (e) {
            console.error("Error merging subsection header cells:", e);
          }

          worksheet.getCell(currentRow, 1).value =
            `  ${subSectionDisplayName.toUpperCase()}`;

          styleCategoryHeaderRow(worksheet, currentRow, headers.length, false);

          // Set alignment AFTER styling to ensure it's not overridden
          worksheet.getCell(currentRow, 1).alignment = {
            horizontal: "left",
            vertical: "middle",
          };

          currentRow++;
        }

        let subSectionTotal = 0;

        items.forEach((item: any) => {
          if (!item.quantity || Math.round(item.quantity) === 0) return;

          // Add data row
          let colIndex = 1;
          dataKeys.forEach((key: string) => {
            let value: any = "";

            switch (key) {
              case "Item":
                value = `${sharedItemNumber}`;
                break;
              case "Description":
                value = item.description || "";
                break;
              case "Qty": {
                const qty = Math.round(item.quantity);
                value = qty !== 0 ? qty : "";
                break;
              }
              case "Unit":
                value = item.unit || "";
                break;
              case "Rate": {
                const rate = item.rate;
                value = rate !== 0 ? `£${rate.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "";
                break;
              }
              case "Amount":
                value = item.amount !== 0 ? `£${Math.abs(item.amount).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "";
                break;
            }

            worksheet.getCell(currentRow, colIndex).value = value;
            colIndex++;
          });

          styleDataRow(worksheet, currentRow, headers.length);
          subSectionTotal += Math.abs(item.amount);
          mainSectionTotal += Math.abs(item.amount);
          currentRow++;
          sharedItemNumber++;
        });

        // Extra rows grafted onto this subsection (or, if it has no
        // subsection of its own, this main section) — written here so they
        // land right after the subsection's real rows and before its total,
        // same position the on-screen table's own `injectExtraRows` puts
        // them. Tries the full "<main> - <sub>" category key first (what
        // matchExtraCategory stores an ambiguous bare-segment match under,
        // e.g. Plot Drives' "Private Macadam" repeating under both "Drives
        // to Binder" and "Drives Surface Course"), then the bare
        // subsection/mainSection name for domains that never had that
        // ambiguity to begin with — only one of the two keys ever actually
        // holds a bucket, so summing both is safe.
        const extraForSubsection =
          flushMatchedExtras(subSectionName ? `${mainSectionName} - ${subSectionName}` : mainSectionName) +
          flushMatchedExtras(subSectionName || mainSectionName);
        subSectionTotal += extraForSubsection;
        mainSectionTotal += extraForSubsection;

        // Add subsection total
        if (
          subSectionName &&
          exportOptions.includeTotals &&
          subSectionTotal > 0
        ) {
          const amountCol = headers.indexOf("Amount") + 1;

          // Merge cells for subsection total label
          if (amountCol > 1) {
            try {
              const lastLabelCol = String.fromCharCode(64 + amountCol - 1);
              worksheet.mergeCells(
                `A${currentRow}:${lastLabelCol}${currentRow}`,
              );
            } catch (e) {
              console.error("Error merging subsection total cells:", e);
            }
          }

          worksheet.getCell(currentRow, 1).value = `  ${subSectionDisplayName} Total`;

          worksheet.getCell(currentRow, amountCol).value = `£${subSectionTotal.toLocaleString("en-GB", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`;
          styleTotalRow(worksheet, currentRow, headers.length);

          // Set alignment AFTER styling to ensure it's not overridden
          worksheet.getCell(currentRow, 1).alignment = {
            horizontal: "left",
            vertical: "middle",
          };

          currentRow++; // advance past total row
          currentRow++; // empty row below total
        }
      });

      // Extra rows grafted onto the MAIN section itself, as opposed to one
      // of its subsections — e.g. GroundworksCutFillBillSummary.tsx's
      // "cut-fill-parent"/"soil-management-parent" headers, whose alias
      // above resolves to this mainSectionName rather than any subSection.
      mainSectionTotal += flushMatchedExtras(mainSectionName);

      // Add main section total
      if (exportOptions.includeTotals && mainSectionTotal > 0) {
        const amountCol = headers.indexOf("Amount") + 1;

        // Merge cells for main section total label
        if (amountCol > 1) {
          try {
            const lastLabelCol = String.fromCharCode(64 + amountCol - 1);
            worksheet.mergeCells(`A${currentRow}:${lastLabelCol}${currentRow}`);
          } catch (e) {
            console.error("Error merging main section total cells:", e);
          }
        }

        worksheet.getCell(currentRow, 1).value = `${mainSectionName} TOTAL`;

        worksheet.getCell(currentRow, amountCol).value = `£${mainSectionTotal.toLocaleString("en-GB", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`;
        styleTotalRow(worksheet, currentRow, headers.length, true);

        // Set alignment AFTER styling to ensure it's not overridden
        worksheet.getCell(currentRow, 1).alignment = {
          horizontal: "left",
          vertical: "middle",
        };

        currentRow++; // advance past total row
        currentRow++; // empty row below total
      }
    });
  } else {
    // Non-hierarchical categories - use original logic
    boqItems.forEach((item: any) => {
      if (!item.quantity || Math.round(item.quantity) === 0) return;

      // Add category header if category changed
      if (item.category && item.category !== currentCategory) {
        // Extra rows grafted onto the OUTGOING category — flushed here so
        // they land right after its real rows and before its subtotal, same
        // position the on-screen table's own `injectExtraRows` puts them.
        if (currentCategory) {
          categoryTotal += flushMatchedExtras(currentCategory);
        }

        if (
          currentCategory &&
          categoryTotal > 0 &&
          exportOptions.includeTotals
        ) {
          // Add category subtotal (only if includeTotals is true)
          const amountCol = headers.indexOf("Amount") + 1;

          // Merge cells for category total label
          if (amountCol > 1) {
            try {
              const lastLabelCol = String.fromCharCode(64 + amountCol - 1);
              worksheet.mergeCells(
                `A${currentRow}:${lastLabelCol}${currentRow}`,
              );
            } catch (e) {
              console.error("Error merging category total cells:", e);
            }
          }

          worksheet.getCell(currentRow, 1).value = `${currentCategory} Total`;

          worksheet.getCell(currentRow, amountCol).value = `£${categoryTotal.toLocaleString("en-GB", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`;
          styleTotalRow(worksheet, currentRow, headers.length);

          // Set alignment AFTER styling to ensure it's not overridden
          worksheet.getCell(currentRow, 1).alignment = {
            horizontal: "left",
            vertical: "middle",
          };

          currentRow++; // advance past total row
          currentRow++; // empty row below total
        }

        currentCategory = item.category;
        categoryTotal = 0;

        // Add category header - merge cells to span all columns
        try {
          worksheet.mergeCells(currentRow, 1, currentRow, headers.length);
        } catch (e) {
          console.error("Error merging category header cells:", e);
        }

        worksheet.getCell(currentRow, 1).value = item.category.toUpperCase();

        styleCategoryHeaderRow(worksheet, currentRow, headers.length, true);

        // Set alignment AFTER styling to ensure it's not overridden
        worksheet.getCell(currentRow, 1).alignment = {
          horizontal: "left",
          vertical: "middle",
        };

        currentRow++;
      }

      // Add data row
      let colIndex = 1;
      dataKeys.forEach((key: string) => {
        let value: any = "";

        switch (key) {
          case "Item":
            value = `${sharedItemNumber}`;
            break;
          case "Description":
            value = item.description || "";
            break;
          case "Qty": {
            const qty = Math.round(item.quantity);
            value = qty !== 0 ? qty : "";
            break;
          }
          case "Unit":
            value = item.unit || "";
            break;
          case "Rate": {
            const rate = item.rate;
            value = rate !== 0 ? `£${rate.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "";
            break;
          }
          case "Amount":
            value = item.amount !== 0 ? `£${Math.abs(item.amount).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "";
            break;
        }

        worksheet.getCell(currentRow, colIndex).value = value;
        colIndex++;
      });

      styleDataRow(worksheet, currentRow, headers.length);
      categoryTotal += Math.abs(item.amount);
      currentRow++;
      sharedItemNumber++;
    });

    // Extra rows grafted onto the LAST category, which never went through
    // the "category changed" branch above.
    if (currentCategory) {
      categoryTotal += flushMatchedExtras(currentCategory);
    }

    // Add final category total (only if includeTotals is true)
    if (currentCategory && categoryTotal > 0 && exportOptions.includeTotals) {
      const amountCol = headers.indexOf("Amount") + 1;

      // Merge cells for final category total label
      if (amountCol > 1) {
        try {
          const lastLabelCol = String.fromCharCode(64 + amountCol - 1);
          worksheet.mergeCells(`A${currentRow}:${lastLabelCol}${currentRow}`);
        } catch (e) {
          console.error("Error merging final category total cells:", e);
        }
      }

      worksheet.getCell(currentRow, 1).value = `${currentCategory} Total`;

      worksheet.getCell(currentRow, amountCol).value = `£${categoryTotal.toLocaleString("en-GB", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
      styleTotalRow(worksheet, currentRow, headers.length);

      // Set alignment AFTER styling to ensure it's not overridden
      worksheet.getCell(currentRow, 1).alignment = {
        horizontal: "left",
        vertical: "middle",
      };

      currentRow++; // advance past total row
      currentRow++; // empty row below total
    }
  }

  // Per-group subtotal, matching the convention every real category/
  // subsection block in this sheet already follows (its own rows, then its
  // own total row) — computed as this group's own delta on `extraItemsTotal`
  // rather than the whole sheet's cumulative figure, so it isn't mistaken for
  // an amount on top of what the matched categories above already total.
  const writeExtraGroupTotal = (label: string, amount: number) => {
    if (!exportOptions.includeTotals || amount === 0) return;
    const amountCol = headers.indexOf("Amount") + 1;
    if (amountCol > 1) {
      try {
        const lastLabelCol = String.fromCharCode(64 + amountCol - 1);
        worksheet.mergeCells(`A${currentRow}:${lastLabelCol}${currentRow}`);
      } catch (e) {
        console.error("Error merging Additional Items group total cells:", e);
      }
    }
    worksheet.getCell(currentRow, 1).value = `${label} Total`;
    worksheet.getCell(currentRow, amountCol).value = `£${amount.toLocaleString("en-GB", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
    styleTotalRow(worksheet, currentRow, headers.length);
    worksheet.getCell(currentRow, 1).alignment = { horizontal: "left", vertical: "middle" };
    currentRow++;
    currentRow++; // empty row below total
  };

  // Categories/subsections that had a matching extra bucket but never
  // actually rendered above (every fixed row in that category is currently
  // zero-qty/hidden, so the category never got a header of its own here) —
  // still shown under that category's own name, so nothing is lost.
  matchedExtrasByCategory.forEach((nodes, categoryName) => {
    writeExtraGroupHeader(categoryName.toUpperCase());
    const before = extraItemsTotal;
    nodes.forEach((node) => writeExtraNode(node, 1));
    writeExtraGroupTotal(categoryName, extraItemsTotal - before);
  });

  // Items whose attach-point row can't be matched on this sheet at all —
  // same generic fallback wording the on-screen table itself uses for this
  // same situation, so nothing is silently dropped.
  orphanedExtraBuckets.forEach(({ nodes }) => {
    writeExtraGroupHeader("ADDITIONAL ITEMS");
    const before = extraItemsTotal;
    nodes.forEach((node) => writeExtraNode(node, 1));
    writeExtraGroupTotal("Additional Items", extraItemsTotal - before);
  });

  // Brand-new top-level sections/items (root "Add Section"/"Add Item") —
  // each shows under its own name, exactly as it would as a new section on
  // screen, with no wrapper header.
  rootExtraNodes.forEach((node) => writeExtraNode(node, 0));

  // Add grand total (only if includeTotals is true)
  if (exportOptions.includeTotals) {
    const grandTotal = boqItems.reduce(
      (sum: number, item: any) => sum + Math.abs(item.amount),
      0,
    ) + extraItemsTotal;
    const amountCol = headers.indexOf("Amount") + 1;

    // Merge cells for grand total label to span all columns except amount column
    if (amountCol > 1) {
      try {
        const lastLabelCol = String.fromCharCode(64 + amountCol - 1);
        worksheet.mergeCells(`A${currentRow}:${lastLabelCol}${currentRow}`);
      } catch (e) {
        console.error("Error merging grand total cells:", e);
      }
    }

    worksheet.getCell(currentRow, 1).value = `${section.name} GRAND TOTAL`;

    worksheet.getCell(currentRow, amountCol).value = `£${grandTotal.toLocaleString("en-GB", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
    styleTotalRow(worksheet, currentRow, headers.length, true);

    // Set alignment AFTER styling to ensure it's not overridden
    worksheet.getCell(currentRow, 1).alignment = {
      horizontal: "left",
      vertical: "middle",
    };
  }

  // Auto-fit columns: description capped at 80, Amount column guaranteed min 16, others capped at 20
  {
    const descColIndex = headers.indexOf('Description') + 1;
    const amountColIndex = headers.indexOf('Amount') + 1;
    const colMaxWidths: Record<number, number> = {};
    const colMinWidths: Record<number, number> = {};
    headers.forEach((_h: string, i: number) => {
      const colNum = i + 1;
      colMaxWidths[colNum] = colNum === descColIndex ? 80 : 25;
      if (colNum === amountColIndex) colMinWidths[colNum] = 16;
    });
    autoFitColumns(worksheet, { columnMaxWidths: colMaxWidths, columnMinWidths: colMinWidths, defaultMin: 6, padding: 2, minTotalWidth: 100 });
  }

  // Set row heights for better readability (apply to data rows only, skip header rows 1-7)
  worksheet.eachRow((row) => {
    if (row.number > 7) {
      // Skip logo and header rows (1-7)
      // Calculate height based on description cell content if it exists
      let maxHeight = 26; // Minimum height for data rows

      row.eachCell((cell, colNumber) => {
        // Check description column (usually column 2)
        if (cell.value && typeof cell.value === "string" && colNumber === 2) {
          const lineCount = (cell.value.match(/\n/g) || []).length + 1;
          const charsPerLine = 40; // Column width is ~40 units for description
          const estimatedWrappedLines = Math.ceil(
            cell.value.length / charsPerLine,
          );
          const totalLines = Math.max(lineCount, estimatedWrappedLines);
          const extraPadding = 6;
          const calculatedHeight = Math.max(26, totalLines * 16 + extraPadding);
          maxHeight = Math.max(maxHeight, calculatedHeight);
        }
      });

      row.height = maxHeight;
    }
  });

  // Enable text wrapping for data cells only (skip header rows 1-7 and category headers)
  worksheet.eachRow((row, rowNumber) => {
    if (row.number <= 7) {
      // Skip header rows - they have their own alignment set
      return;
    }

    row.eachCell((cell) => {
      // Skip cells that are part of merged ranges (category headers, totals)
      if (cell.isMerged) {
        return;
      }

      // Determine horizontal alignment based on column header
      // Item(A), Qty(C), Unit(D), Rate(E) → center; Description(B) → left; Amount(F) → right
      const descColNum = headers.indexOf("Description") + 1;
      const amountColNum = headers.indexOf("Amount") + 1;
      let horizontalAlign: "left" | "center" | "right" = "center";
      if (descColNum > 0 && cell.col === descColNum) {
        horizontalAlign = "left";
      } else if (amountColNum > 0 && cell.col === amountColNum) {
        horizontalAlign = "right";
      }

      cell.alignment = {
        ...cell.alignment,
        wrapText: true,
        vertical: "middle",
        horizontal: horizontalAlign,
        shrinkToFit: false,
      };

      // Ensure font is set to enable proper wrapping
      if (!cell.font || !cell.font.size) {
        cell.font = { ...cell.font, size: 11 };
      }
    });
  });

  // Set page setup for printing - fit to one page width
  worksheet.pageSetup = {
    paperSize: 9, // A4
    orientation: "portrait",
    fitToPage: true,
    fitToWidth: 1,
    fitToHeight: 0,
    scale: 95, // Slightly reduce scale for better fit
    margins: {
      left: 0.3,
      right: 0.3,
      top: 0.4,
      bottom: 0.4,
      header: 0.15,
      footer: 0.15,
    },
    horizontalCentered: true,
  };
}

/**
 * Excel worksheet names can't contain : \ / ? * [ ] and are capped at 31
 * characters, and every sheet in a workbook must have a unique name — none
 * of which a user-typed Dynamic Bill Summary tab label is guaranteed to
 * satisfy. `usedNames` tracks lower-cased names already claimed (seeded with
 * the fixed sheets' own names by the caller) so a collision gets a
 * " (2)"-style suffix instead of ExcelJS throwing on `addWorksheet`.
 */
function sanitizeWorksheetName(label: string, usedNames: Set<string>): string {
  const base = (label.replace(/[:\\/?*[\]]/g, "").trim() || "Bill Summary").slice(0, 31);
  let candidate = base;
  let suffixIndex = 2;
  while (usedNames.has(candidate.toLowerCase())) {
    const suffix = ` (${suffixIndex})`;
    candidate = `${base.slice(0, 31 - suffix.length)}${suffix}`;
    suffixIndex++;
  }
  usedNames.add(candidate.toLowerCase());
  return candidate;
}

/**
 * Create one worksheet per Dynamic Bill Summary tab, styled to match the
 * fixed Bill Summary sheets' own hierarchical categorisation exactly (e.g.
 * Cut & Fill's own export sheet, `createBillSummarySheet`'s `hasHierarchy`
 * branch): a top-level `DynamicBillSummarySectionNode` renders as a dark-navy
 * bold-white MAIN section header, a nested one as a lighter sub-header
 * (`styleCategoryHeaderRow`), and each section (main or nested) gets its own
 * "<name> Total"/"<NAME> TOTAL" subtotal row (`styleTotalRow`) right after
 * its own content, same as every fixed sheet's section blocks. Leaf
 * `DynamicBillSummaryItemNode` rows render as data rows with a
 * computeDynamicBillSummaryNodeAmount/computeDynamicBillSummaryNodeRate'd
 * Rate/Amount — the same functions the live panel and Sum Analysis use, so
 * the exported figures can't drift from what's on screen. A "text"-type item
 * shows its free-typed `amountText` in the Amount column and contributes £0
 * to totals, matching the panel and `computeDynamicBillSummaryNodeAmount`'s
 * own text short-circuit.
 */
async function createDynamicBillSummarySheet(
  workbook: any,
  logoImageId: any,
  tab: DynamicBillSummaryTab,
  projectInfo: any,
  primaryColor: string,
  exportOptions: ExportDetailOptions,
  markupFactor: number,
  sheetName: string,
) {
  const worksheet = workbook.addWorksheet(sheetName);

  const { headers, dataKeys } = buildHeaders(exportOptions);

  const headerRowNum = createWorksheetHeader(
    worksheet,
    logoImageId,
    tab.label,
    projectInfo.projectName,
    projectInfo.clientName,
    projectInfo.region,
    primaryColor,
    headers.length,
  );

  styleColumnHeaders(worksheet, headers, headerRowNum, primaryColor);

  let currentRow = headerRowNum + 1;

  // "Item" column numbering — one continuous serial count (1, 2, 3, 4...)
  // across every leaf item in the whole tab, regardless of which section
  // it's under; a section header itself never consumes a number, and
  // entering a new section does NOT reset the count back to 1.
  let nextItemNumber = 1;

  // "<Label> Total"/"<Label> TOTAL" rows use Title Case on the section's own
  // name regardless of how the user typed it in the Add Section modal (a
  // free-typed field, unlike the fixed sheets' own curated category names) —
  // only the "TOTAL"/"GRAND TOTAL" suffix itself stays literally uppercase.
  const toTitleCase = (label: string): string =>
    label.replace(/\w\S*/g, (word) => `${word.charAt(0).toUpperCase()}${word.slice(1).toLowerCase()}`);

  const writeSection = (node: Extract<DynamicBillSummaryNode, { type: "section" }>, depth: number) => {
    // Same two-tier header convention every fixed Bill Summary's own
    // hierarchical sheet uses (e.g. Cut & Fill's "CUT & FILL"/"SITE
    // CLEARANCE" main headers vs its "FOUNDS"/"PATHS" sub-headers,
    // createBillSummarySheet's `hasHierarchy` branch): a top-level section
    // gets the dark-navy/bold-white main-header override on top of
    // styleCategoryHeaderRow's own look, a nested section stays at
    // styleCategoryHeaderRow's lighter sub-header styling. Every section
    // shows only ITS OWN label, never its parent's — the parent's name is
    // already established by its own header row above (plus indentation),
    // so re-prefixing a child's header with it would just repeat what's
    // already there. (PlotDrivesBillSummary.tsx's own sub-headers look like
    // "PRIVATE MACADAM - DRIVES TO BINDER", but that's because its
    // `_fullSectionLabel` is the pre-remap ORIGINAL category string, not a
    // deliberately reconstructed "parent - child" — Cut & Fill's own
    // sub-headers ("FOUNDS", "PATHS") show no such prefix.)
    const isMainSection = depth === 0;
    try {
      worksheet.mergeCells(currentRow, 1, currentRow, headers.length);
    } catch (e) {
      console.error("Error merging Dynamic Bill Summary section header cells:", e);
    }
    worksheet.getCell(currentRow, 1).value = `${"  ".repeat(depth)}${node.label.toUpperCase()}`;
    styleCategoryHeaderRow(worksheet, currentRow, headers.length, isMainSection);
    if (isMainSection) {
      for (let i = 1; i <= headers.length; i++) {
        worksheet.getCell(currentRow, i).fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FF003366" },
        };
        worksheet.getCell(currentRow, i).font = {
          bold: true,
          color: { argb: "FFFFFFFF" },
        };
      }
    }
    worksheet.getCell(currentRow, 1).alignment = { horizontal: "left", vertical: "middle" };
    currentRow++;
    // Item numbering restarts at 1 for every section's own children, rather
    // than continuing a running count from its siblings or its parent.
    writeNodeList(node.children, depth + 1);

    // Section subtotal, same "<name> Total"/"<NAME> TOTAL" +
    // styleTotalRow(..., isMainSection) convention every fixed sheet's own
    // section blocks use — a main section's total reads bolder/gold
    // (isGrandTotal styling), a nested section's stays the lighter
    // subsection-total look. The name itself is Title Cased regardless of
    // how the user typed it in the Add Section modal — only the "TOTAL"
    // suffix is literally uppercase.
    if (exportOptions.includeTotals) {
      const sectionAmount = computeDynamicBillSummaryNodeAmount(node, markupFactor);
      if (sectionAmount !== 0) {
        const titleCasedLabel = toTitleCase(node.label);
        const amountCol = headers.indexOf("Amount") + 1;
        if (amountCol > 1) {
          try {
            const lastLabelCol = String.fromCharCode(64 + amountCol - 1);
            worksheet.mergeCells(`A${currentRow}:${lastLabelCol}${currentRow}`);
          } catch (e) {
            console.error("Error merging Dynamic Bill Summary section total cells:", e);
          }
        }
        worksheet.getCell(currentRow, 1).value = isMainSection
          ? `${titleCasedLabel} TOTAL`
          : `${"  ".repeat(depth)}${titleCasedLabel} Total`;
        worksheet.getCell(currentRow, amountCol).value = `£${sectionAmount.toLocaleString("en-GB", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`;
        styleTotalRow(worksheet, currentRow, headers.length, isMainSection);
        worksheet.getCell(currentRow, 1).alignment = { horizontal: "left", vertical: "middle" };
        currentRow++; // advance past total row
        currentRow++; // empty row below total — every section's own total gets this
        // spacer in the fixed sheets (both subsection AND main-section totals),
        // not just top-level ones, so sections never run straight into each other.
      }
    }
  };

  const writeItem = (
    node: Extract<DynamicBillSummaryNode, { type: "item" }>,
    depth: number,
  ) => {
    const isText = node.amountType === "text";
    const amount = computeDynamicBillSummaryNodeAmount(node, markupFactor);
    const rate = !isText && node.rate !== undefined ? computeDynamicBillSummaryNodeRate(node.rate, markupFactor) : undefined;

    let colIndex = 1;
    dataKeys.forEach((key: string) => {
      let value: any = "";
      switch (key) {
        case "Item":
          value = `${nextItemNumber}`;
          break;
        case "Description":
          value = `${"  ".repeat(depth)}${node.description || ""}`;
          break;
        case "Qty": {
          const qty = !isText && node.qty ? Math.round(node.qty) : 0;
          value = qty !== 0 ? qty : "";
          break;
        }
        case "Unit":
          value = isText ? "" : node.unit || "";
          break;
        case "Rate":
          value = rate ? `£${rate.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "";
          break;
        case "Amount":
          value = isText
            ? node.amountText || ""
            : amount !== 0
              ? `£${Math.abs(amount).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
              : "";
          break;
      }
      worksheet.getCell(currentRow, colIndex).value = value;
      colIndex++;
    });

    styleDataRow(worksheet, currentRow, headers.length);
    nextItemNumber++;
    currentRow++;
  };

  // Sections don't themselves consume an "Item" number — only leaf items do,
  // via the single shared `nextItemNumber` counter declared above — so
  // numbering runs serially (1, 2, 3, 4...) through the whole tab and never
  // resets when a new section starts.
  const writeNodeList = (nodes: DynamicBillSummaryNode[], depth: number) => {
    nodes.forEach((node) => {
      if (node.type === "section") {
        writeSection(node, depth);
      } else {
        writeItem(node, depth);
      }
    });
  };

  writeNodeList(tab.nodes, 0);

  if (exportOptions.includeTotals) {
    const grandTotal = tab.nodes.reduce(
      (sum, node) => sum + computeDynamicBillSummaryNodeAmount(node, markupFactor),
      0,
    );
    const amountCol = headers.indexOf("Amount") + 1;
    if (amountCol > 1) {
      try {
        const lastLabelCol = String.fromCharCode(64 + amountCol - 1);
        worksheet.mergeCells(`A${currentRow}:${lastLabelCol}${currentRow}`);
      } catch (e) {
        console.error("Error merging Dynamic Bill Summary grand total cells:", e);
      }
    }
    worksheet.getCell(currentRow, 1).value = `${toTitleCase(tab.label)} GRAND TOTAL`;
    worksheet.getCell(currentRow, amountCol).value = `£${grandTotal.toLocaleString("en-GB", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
    styleTotalRow(worksheet, currentRow, headers.length, true);
    worksheet.getCell(currentRow, 1).alignment = { horizontal: "left", vertical: "middle" };
  }

  const descColIndex = headers.indexOf("Description") + 1;
  const amountColIndex = headers.indexOf("Amount") + 1;
  const colMaxWidths: Record<number, number> = {};
  const colMinWidths: Record<number, number> = {};
  headers.forEach((_h: string, i: number) => {
    const colNum = i + 1;
    colMaxWidths[colNum] = colNum === descColIndex ? 80 : 25;
    if (colNum === amountColIndex) colMinWidths[colNum] = 16;
  });
  autoFitColumns(worksheet, { columnMaxWidths: colMaxWidths, columnMinWidths: colMinWidths, defaultMin: 6, padding: 2, minTotalWidth: 100 });

  // Row heights and text-wrapping/alignment — same post-processing pass
  // every fixed Bill Summary sheet gets in createBillSummarySheet (e.g. Cut
  // & Fill, Plot Drives): description-driven row height, and column
  // alignment by type (Description left, Amount right, everything else
  // centred), rather than fixed-height unwrapped rows.
  worksheet.eachRow((row: any) => {
    if (row.number > headerRowNum) {
      let maxHeight = 26;
      row.eachCell((cell: any, colNumber: any) => {
        if (cell.value && typeof cell.value === "string" && colNumber === 2) {
          const lineCount = (cell.value.match(/\n/g) || []).length + 1;
          const charsPerLine = 40;
          const estimatedWrappedLines = Math.ceil(cell.value.length / charsPerLine);
          const totalLines = Math.max(lineCount, estimatedWrappedLines);
          const extraPadding = 6;
          const calculatedHeight = Math.max(26, totalLines * 16 + extraPadding);
          maxHeight = Math.max(maxHeight, calculatedHeight);
        }
      });
      row.height = maxHeight;
    }
  });

  worksheet.eachRow((row: any) => {
    if (row.number <= headerRowNum) return;
    row.eachCell((cell: any) => {
      if (cell.isMerged) return;
      let horizontalAlign: "left" | "center" | "right" = "center";
      if (descColIndex > 0 && cell.col === descColIndex) {
        horizontalAlign = "left";
      } else if (amountColIndex > 0 && cell.col === amountColIndex) {
        horizontalAlign = "right";
      }
      cell.alignment = {
        ...cell.alignment,
        wrapText: true,
        vertical: "middle",
        horizontal: horizontalAlign,
        shrinkToFit: false,
      };
      if (!cell.font || !cell.font.size) {
        cell.font = { ...cell.font, size: 11 };
      }
    });
  });

  worksheet.pageSetup = {
    paperSize: 9,
    orientation: "portrait",
    fitToPage: true,
    fitToWidth: 1,
    fitToHeight: 0,
    scale: 95,
    margins: {
      left: 0.3,
      right: 0.3,
      top: 0.4,
      bottom: 0.4,
      header: 0.15,
      footer: 0.15,
    },
    horizontalCentered: true,
  };
}

/**
 * Create Plot Matrix worksheet
 * Uses pre-calculated columns and rows if provided (for exact UI match)
 */
async function createPlotMatrixSheet(
  workbook: any,
  logoImageId: any,
  groundworksData: any,
  projectInfo: any,
  primaryColor: string,
  ratesMapping?: RateMappingJSON | null,
  itemLibraryData?: ItemLibraryResponse,
  preCalculatedColumns?: any[],
  preCalculatedRows?: any[],
  viewMode: 'plot' | 'stage' = 'plot',
  settings?: any,
  markup?: PlotMatrixMarkup,
  persistedRetSpecs?: RetainingWallsBuildUpSpecification[],
  persistedGeoSpecs?: GeoToberWallBuildUpSpecification[],
) {
  const sheetName = viewMode === 'stage' ? 'Valuation Stage Matrix' : 'Plot Matrix';
  const worksheet = workbook.addWorksheet(sheetName);

  // Effective markup percentages — only applied if non-zero. Caller computes
  // these from globalSettings + the user's include flags (or passes
  // 0 to disable). Defined here so both the BoQ export path (which
  // generates rows fresh) and the standalone path (which passes
  // pre-calculated uplifted rows) share the same discount-row logic
  // below.
  const prelimsPct = markup?.prelimsPct ?? 0;
  const ohpPct = markup?.ohpPct ?? 0;
  const discountPct = markup?.discountPct ?? 0;
  const upliftFactor = (1 + ohpPct / 100) * (1 + prelimsPct / 100);

  // Use pre-calculated data if provided, otherwise generate fresh. When
  // generating fresh, pass `upliftFactor` straight into
  // `generatePlotMatrixCategoryRows` so the prelims+OH&P markup is applied
  // inline by the row generator — every step's cells (Site Strip, Cut/Fill,
  // Foundations, Scaffold, Drainage, Plot Services, Plot Pavings, Garden,
  // Ret & Screen Walls, Plot Drives, etc.) get the uniform `(1 + prelim% +
  // ohp%)` factor in a single pass. When preCalculated* is provided
  // (standalone Plot Matrix export), the caller has already applied both —
  // we use them as-is.
  const categoryColumns =
    preCalculatedColumns ||
    generatePlotMatrixCategoryColumns(groundworksData);
  const soilBoQTotals = computeSoilBoQTotals(groundworksData, itemLibraryData);
  const baseOhpPercent = settings?.groundworks?.overheadAndProfit ?? 0;

  // Build per-section BoQ targets with markupFactor baked into rates —
  // matches Sum Analysis rounding path exactly (round(rate × mk, 2) × qty).
  // Post-multiplying base totals gives a different result due to rounding.
  let boqStepTargets: BoqStepTargets | undefined;
  if (!preCalculatedRows && itemLibraryData) {
    const cutFillFieldData = computeCutFillFieldBillSummaryData(groundworksData, itemLibraryData);
    const fill6F2Amount = cutFillFieldData.get('fillToMakeUp6F2')?.billSummaryAmount ?? 0;
    const fill6F2MarkedUp = fill6F2Amount * upliftFactor;
    boqStepTargets = new Map<string, number>([
      ['Cut & Fills',      calculateCutFillGrandTotal(groundworksData, ratesMapping, itemLibraryData, upliftFactor) - fill6F2MarkedUp],
      ['Foundations',      calculateFoundationsGrandTotal(groundworksData, ratesMapping, itemLibraryData, upliftFactor)],
      ['Drainage',         calculateDrainageGrandTotal(groundworksData, ratesMapping, itemLibraryData, upliftFactor)],
      ['Plot Drives',      calculatePlotDrivesGrandTotal(groundworksData, ratesMapping, itemLibraryData, upliftFactor) + fill6F2MarkedUp],
      ['Plot Service',     calculatePlotServiceGrandTotal(groundworksData, ratesMapping, itemLibraryData, upliftFactor)],
      ['Plot Pavings',     calculatePlotPavingsGrandTotal(groundworksData, ratesMapping, itemLibraryData, upliftFactor)],
      ['Scaffold',         calculateScaffoldGrandTotal(groundworksData, ratesMapping, itemLibraryData, upliftFactor)],
      ['Garden',           calculateGardenGrandTotal(groundworksData, ratesMapping, itemLibraryData, upliftFactor)],
      ['Ret & Screen Walls',
        calculateRetScreenWallsGrandTotal(groundworksData, ratesMapping, itemLibraryData, persistedRetSpecs, upliftFactor) +
        calculateGEOToberWallGrandTotal(groundworksData, ratesMapping, itemLibraryData, persistedGeoSpecs, upliftFactor)],
    ]);
  }

  // Standalone Plot Matrix export (preCalculatedRows provided) already has
  // the Dynamic Bill Summary / fixed-summary extra-item `plotMapping`
  // overlay baked in — PlotMatrix.tsx's own `getExportPayload()` exports
  // `displayPlotMatrixData`, which is `applyDynamicBillSummaryToPlotMatrixRows`'s
  // own output (see PlotMatrix.tsx). Applying the overlay again here would
  // double-count every mapped item's amount, so it's only applied when rows
  // are generated fresh (the full Groundworks BoQ export path).
  const plotMatrixRows =
    preCalculatedRows ||
    applyDynamicBillSummaryToPlotMatrixRows(
      generatePlotMatrixCategoryRows(
        groundworksData,
        categoryColumns,
        ratesMapping,
        itemLibraryData,
        soilBoQTotals,
        baseOhpPercent,
        undefined,
        upliftFactor,
        undefined,
        boqStepTargets,
      ),
      categoryColumns,
      groundworksData?.dynamicBillSummaryTabs ?? [],
      upliftFactor,
      groundworksData?.cutFillsExtraItems,
      groundworksData?.foundationsExtraItems,
      groundworksData?.drainageExtraItems,
      groundworksData?.plotDrivesExtraItems,
      groundworksData?.plotServiceExtraItems,
      groundworksData?.scaffoldExtraItems,
      groundworksData?.plotPavingsExtraItems,
      groundworksData?.gardenExtraItems,
      groundworksData?.retainingWallsExtraItems,
      groundworksData?.geoWallsExtraItems,
    );

  if (!plotMatrixRows || plotMatrixRows.length === 0) {
    return;
  }

  // Build grouped headers - match UI format with main headers and sub-headers
  const dataKeys = ["plotNo", "housetype"];

  // Group columns by main header (same logic as PlotMatrix UI)
  const groupedColumns: { [key: string]: any[] } = {};

  categoryColumns.forEach((col: any) => {
    // Parse category name: "Site Strip - Strip to remove Vegetation" or "Cut/Fill - Cut/Fill to Foundations"
    const parts = col.categoryName.split(" - ");
    const hasSeparator = parts.length > 1;
    const mainHeader = parts[0] || col.categoryName;
    const subHeader = parts[1] || col.categoryName;

    if (!groupedColumns[mainHeader]) {
      groupedColumns[mainHeader] = [];
    }

    groupedColumns[mainHeader].push({
      subHeader,
      hasSeparator,
      dataIndex: col.dataIndex,
      categoryName: col.categoryName,
    });

    dataKeys.push(col.dataIndex);
  });

  // Add total column
  dataKeys.push("rowTotal");

  // Build a lookup from dataIndex → categoryName so the cell writer can check INCLUDED_SUB_STAGES
  const dataIndexToCategoryName: { [dataIndex: string]: string } = {};
  categoryColumns.forEach((col: any) => {
    dataIndexToCategoryName[col.dataIndex] = col.categoryName;
  });

  // Calculate total number of columns
  const totalColumns = 2 + categoryColumns.length + 1; // Plot No + House Type + categories + Total

  // Create header with correct number of columns
  // Logo row merged to BW (column 75) to span the wide plot matrix
  const logoHeaderRowNum = createWorksheetHeader(
    worksheet,
    logoImageId,
    viewMode === 'stage' ? 'Valuation Stage Matrix - Cost per Plot' : 'Plot Matrix',
    projectInfo.projectName,
    projectInfo.clientName,
    projectInfo.region,
    primaryColor,
    totalColumns,
    undefined,
    75, // BW column — logo row spans full width of the matrix
  );

  // Create two-tier headers (main headers and sub-headers)
  const mainHeaderRowNum = logoHeaderRowNum;
  const subHeaderRowNum = logoHeaderRowNum + 1;

  let colIndex = 1;

  // First column group: "Valuation Stage" with "Plot No" and "House Type" sub-headers
  // Main header
  worksheet.mergeCells(mainHeaderRowNum, 1, mainHeaderRowNum, 2);
  const valuationCell = worksheet.getCell(mainHeaderRowNum, 1);
  valuationCell.value = "Valuation Stage";
  valuationCell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: primaryColor }, // Same color as sub-headers
  };
  valuationCell.font = {
    name: "Calibri",
    size: 12,
    bold: true,
    color: { argb: "FFFFFF" },
  };
  valuationCell.alignment = {
    vertical: "middle",
    horizontal: "center",
    wrapText: true,
  };
  valuationCell.border = {
    top: { style: "medium", color: { argb: "000000" } },
    left: { style: "medium", color: { argb: "000000" } },
    bottom: { style: "medium", color: { argb: "000000" } },
    right: { style: "medium", color: { argb: "000000" } },
  };

  // Apply borders to all cells in the merged Valuation Stage range
  for (let col = 1; col <= 2; col++) {
    const cell = worksheet.getCell(mainHeaderRowNum, col);
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: primaryColor },
    };
    cell.font = {
      name: "Calibri",
      size: 12,
      bold: true,
      color: { argb: "FFFFFF" },
    };
    cell.border = {
      top: { style: "medium", color: { argb: "000000" } },
      left:
        col === 1
          ? { style: "medium", color: { argb: "000000" } }
          : { style: "thin", color: { argb: primaryColor } },
      bottom: { style: "medium", color: { argb: "000000" } },
      right:
        col === 2
          ? { style: "medium", color: { argb: "000000" } }
          : { style: "thin", color: { argb: primaryColor } },
    };
  }

  // Sub-headers for Valuation Stage
  ["Plot No", "House Type"].forEach((header, idx) => {
    const cell = worksheet.getCell(subHeaderRowNum, idx + 1);
    cell.value = header;
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: primaryColor },
    };
    cell.font = {
      name: "Calibri",
      size: 11,
      bold: true,
      color: { argb: "FFFFFF" },
    };
    cell.alignment = {
      vertical: "middle",
      horizontal: "center",
      wrapText: true,
    };
    cell.border = {
      top: { style: "thin", color: { argb: primaryColor } },
      left:
        idx === 0
          ? { style: "medium", color: { argb: "000000" } }
          : { style: "medium", color: { argb: "000000" } },
      bottom: { style: "thin", color: { argb: primaryColor } },
      right:
        idx === 1
          ? { style: "medium", color: { argb: "000000" } }
          : { style: "medium", color: { argb: "000000" } },
    };
  });

  colIndex = 3; // Start after Plot No and House Type

  // Add grouped category headers
  Object.keys(groupedColumns).forEach((mainHeader) => {
    const columns = groupedColumns[mainHeader];
    const startCol = colIndex;
    const endCol = colIndex + columns.length - 1;

    // Vertically-merge main+sub header only for the trailing "Uncategorized"
    // placeholder (categoryName has no " - " separator). When the
    // categoryName follows the "Main - Sub" pattern — even if Main === Sub
    // (e.g. "Plot Services - Plot Services") — keep both header rows so the
    // sub-column label remains visible.
    const isSingleMergedColumn =
      columns.length === 1 && columns[0].hasSeparator === false;

    if (isSingleMergedColumn) {
      // Merge both header rows vertically for this single column
      worksheet.mergeCells(mainHeaderRowNum, startCol, subHeaderRowNum, startCol);
      const mergedCell = worksheet.getCell(mainHeaderRowNum, startCol);
      mergedCell.value = mainHeader;
      mergedCell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: primaryColor },
      };
      mergedCell.font = {
        name: "Calibri",
        size: 12,
        bold: true,
        color: { argb: "FFFFFF" },
      };
      mergedCell.alignment = {
        vertical: "middle",
        horizontal: "center",
        wrapText: true,
      };
      mergedCell.border = {
        top: { style: "medium", color: { argb: "000000" } },
        left: { style: "medium", color: { argb: "000000" } },
        bottom: { style: "medium", color: { argb: "000000" } },
        right: { style: "medium", color: { argb: "000000" } },
      };
      // Also style the sub-header row cell for the merged range
      const subMergedCell = worksheet.getCell(subHeaderRowNum, startCol);
      subMergedCell.border = {
        top: { style: "medium", color: { argb: "000000" } },
        left: { style: "medium", color: { argb: "000000" } },
        bottom: { style: "medium", color: { argb: "000000" } },
        right: { style: "medium", color: { argb: "000000" } },
      };
      colIndex++;
    } else {
    // Merge cells for main header if it spans multiple columns
    if (columns.length > 1) {
      worksheet.mergeCells(
        mainHeaderRowNum,
        startCol,
        mainHeaderRowNum,
        endCol,
      );
    }

    // Main header cell
    const mainCell = worksheet.getCell(mainHeaderRowNum, startCol);
    mainCell.value = mainHeader;
    mainCell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: primaryColor }, // Same color as sub-headers
    };
    mainCell.font = {
      name: "Calibri",
      size: 12,
      bold: true,
      color: { argb: "FFFFFF" },
    };
    mainCell.alignment = {
      vertical: "middle",
      horizontal: "center",
      wrapText: true,
    };
    mainCell.border = {
      top: { style: "medium", color: { argb: "000000" } },
      left: { style: "medium", color: { argb: "000000" } },
      bottom: { style: "medium", color: { argb: "000000" } },
      right: { style: "medium", color: { argb: "000000" } },
    };

    // Apply borders to all cells in the merged range
    if (columns.length > 1) {
      for (let col = startCol; col <= endCol; col++) {
        const cell = worksheet.getCell(mainHeaderRowNum, col);
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: primaryColor },
        };
        cell.font = {
          name: "Calibri",
          size: 12,
          bold: true,
          color: { argb: "FFFFFF" },
        };
        cell.border = {
          top: { style: "medium", color: { argb: "000000" } },
          left:
            col === startCol
              ? { style: "medium", color: { argb: "000000" } }
              : { style: "thin", color: { argb: primaryColor } },
          bottom: { style: "medium", color: { argb: "000000" } },
          right:
            col === endCol
              ? { style: "medium", color: { argb: "000000" } }
              : { style: "thin", color: { argb: primaryColor } },
        };
      }
    }

    // Sub-headers
    columns.forEach((col, subIdx) => {
      const subCell = worksheet.getCell(subHeaderRowNum, colIndex);
      subCell.value = col.subHeader;
      subCell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: primaryColor },
      };
      subCell.font = {
        name: "Calibri",
        size: 11,
        bold: true,
        color: { argb: "FFFFFF" },
      };
      subCell.alignment = {
        vertical: "middle",
        horizontal: "center",
        wrapText: true,
      };
      subCell.border = {
        top: { style: "thin", color: { argb: primaryColor } },
        left: { style: "medium", color: { argb: "000000" } },
        bottom: { style: "thin", color: { argb: primaryColor } },
        right: { style: "medium", color: { argb: "000000" } },
      };
      colIndex++;
    });
    } // end else (multi-column or different sub-header)
  });

  // Add Total column (spans both header rows)
  worksheet.mergeCells(mainHeaderRowNum, colIndex, subHeaderRowNum, colIndex);
  const totalCell = worksheet.getCell(mainHeaderRowNum, colIndex);
  totalCell.value = "Total";
  totalCell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: primaryColor },
  };
  totalCell.font = {
    name: "Calibri",
    size: 12,
    bold: true,
    color: { argb: "FFFFFF" },
  };
  totalCell.alignment = {
    vertical: "middle",
    horizontal: "center",
    wrapText: true,
  };
  totalCell.border = {
    top: { style: "medium", color: { argb: "000000" } },
    left: { style: "medium", color: { argb: "000000" } },
    bottom: { style: "thin", color: { argb: primaryColor } },
    right: { style: "medium", color: { argb: "000000" } },
  };

  // Apply borders to both cells in the merged Total column
  for (let row = mainHeaderRowNum; row <= subHeaderRowNum; row++) {
    const cell = worksheet.getCell(row, colIndex);
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: primaryColor },
    };
    cell.font = {
      name: "Calibri",
      size: 12,
      bold: true,
      color: { argb: "FFFFFF" },
    };
    cell.border = {
      top:
        row === mainHeaderRowNum
          ? { style: "medium", color: { argb: "000000" } }
          : { style: "thin", color: { argb: primaryColor } },
      left: { style: "medium", color: { argb: "000000" } },
      bottom:
        row === subHeaderRowNum
          ? { style: "thin", color: { argb: primaryColor } }
          : { style: "medium", color: { argb: "000000" } },
      right: { style: "medium", color: { argb: "000000" } },
    };
  }

  worksheet.getRow(mainHeaderRowNum).height = 30;
  worksheet.getRow(subHeaderRowNum).height = 60;

  // Total column minimum width set here; full auto-fit applied after all data rows are written below

  // Add data rows (start after sub-header row)
  let currentRow = subHeaderRowNum + 1;
  let columnTotals = new Array(totalColumns).fill(0);
  let garageSeparatorInserted = false;

  plotMatrixRows.forEach((row: any) => {
    // Insert a GARAGES separator row before the first garage row
    if (!garageSeparatorInserted && row.rowType === 'garage') {
      garageSeparatorInserted = true;

      // Merge all columns in this separator row
      try {
        worksheet.mergeCells(currentRow, 1, currentRow, totalColumns);
      } catch (e) {
        // Ignore merge errors
      }
      const sepCell = worksheet.getCell(currentRow, 1);
      sepCell.value = "GARAGES";
      sepCell.font = { name: "Calibri", size: 11, bold: true, color: { argb: "374151" } };
      sepCell.alignment = { horizontal: "left", vertical: "middle" };
      sepCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "D1D5DB" } };
      sepCell.border = {
        top: { style: "medium", color: { argb: "000000" } },
        left: { style: "medium", color: { argb: "000000" } },
        bottom: { style: "medium", color: { argb: "000000" } },
        right: { style: "medium", color: { argb: "000000" } },
      };
      worksheet.getRow(currentRow).height = 18;
      currentRow++;
    }

    colIndex = 1;

    dataKeys.forEach((key, index) => {
      const cell = worksheet.getCell(currentRow, colIndex);

      if (key === "plotNo" || key === "housetype") {
        // Plot info columns
        cell.value = row[key];
        cell.alignment = { horizontal: "left", vertical: "middle" };
      } else if (key === "rowTotal") {
        // Row total - match UI format with 2 decimal places
        const total = calculateRowTotal(row);
        cell.value = `£${total.toLocaleString("en-GB", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`;
        cell.font = { bold: true };
        cell.alignment = { horizontal: "right", vertical: "middle" };
        columnTotals[index] += total;
      } else {
        // Category columns - match UI format with 2 decimal places
        const cellData = row[key];
        const amount = cellData?.total || 0;

        if (viewMode === 'plot' && INCLUDED_SUB_STAGES.has(dataIndexToCategoryName[key]) && amount === 0) {
          // Display-only "Included" label — formatted as text so Excel does not treat it as a number.
          // The numeric amount is still accumulated into columnTotals below.
          cell.value = "Included";
          cell.numFmt = "@";
        } else if (amount > 0) {
          cell.value = `£${amount.toLocaleString("en-GB", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`;
        } else {
          cell.value = "£0.00";
        }
        cell.alignment = { horizontal: "right", vertical: "middle" };
        columnTotals[index] += amount;
      }

      // Border
      cell.border = {
        top: { style: "thin", color: { argb: "E5E5E5" } },
        left: { style: "thin", color: { argb: "E5E5E5" } },
        bottom: { style: "thin", color: { argb: "E5E5E5" } },
        right: { style: "thin", color: { argb: "E5E5E5" } },
      };

      // Garage row tint (matches UI blue tint) — alternating skipped for garage rows
      if (row.rowType === 'garage') {
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "F0F4FF" } };
      } else if (currentRow % 2 === 0) {
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "F8F9FA" } };
      }

      colIndex++;
    });

    worksheet.getRow(currentRow).height = 20;
    currentRow++;
  });

  // Add totals row
  currentRow++;
  colIndex = 1;

  // Merge first two columns for grand total label (Plot No + House Type)
  try {
    worksheet.mergeCells(`A${currentRow}:B${currentRow}`);
  } catch (e) {
    console.error("Error merging plot matrix grand total cells:", e);
  }

  // Grand total = sum of every category column's accumulated total — mirrors
  // PlotMatrix.tsx renderSummaryRow (plot view): each column total is rounded
  // to 2dp before summing, then the overall sum is rounded to 2dp.
  // columnTotals[0] = plotNo (unused), [1] = housetype (unused),
  // [2..N-2] = category columns, [N-1] = rowTotal (skip to avoid double-count).
  const boqGrandTotal = parseFloat(
    columnTotals
      .slice(2, columnTotals.length - 1)
      .reduce((acc, v) => acc + parseFloat((v || 0).toFixed(2)), 0)
      .toFixed(2),
  );

  dataKeys.forEach((key, index) => {
    const cell = worksheet.getCell(currentRow, colIndex);

    if (index === 0) {
      cell.value = "GRAND TOTAL";
      cell.font = { bold: true, size: 13, color: { argb: "003366" } };
      cell.alignment = { horizontal: "left", vertical: "middle" };
    } else if (index === 1) {
      cell.value = "";
      cell.font = { bold: true, size: 13, color: { argb: "003366" } };
    } else if (key === "rowTotal") {
      // Use BOQ grand total for the Total column (matches UI exactly)
      cell.value = `£${boqGrandTotal.toLocaleString("en-GB", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
      cell.font = { bold: true, size: 13, color: { argb: "003366" } };
      cell.alignment = { horizontal: "right", vertical: "middle" };
    } else {
      // Column totals for category columns — round to 2dp to match PlotMatrix.tsx
      // renderSummaryRow which does parseFloat(totalSum.toFixed(2)) per column.
      const total = parseFloat((columnTotals[index] || 0).toFixed(2));
      if (total > 0) {
        cell.value = `£${total.toLocaleString("en-GB", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`;
      } else {
        cell.value = "£0.00";
      }
      cell.font = { bold: true, size: 13, color: { argb: "003366" } };
      cell.alignment = { horizontal: "right", vertical: "middle" };
    }

    // Gold styling for totals row
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFD700" },
    };
    cell.border = {
      top: { style: "medium", color: { argb: "000000" } },
      left: { style: "thin", color: { argb: "000000" } },
      bottom: { style: "medium", color: { argb: "000000" } },
      right: { style: "thin", color: { argb: "000000" } },
    };

    colIndex++;
  });

  worksheet.getRow(currentRow).height = 25;

  // DISCOUNT + TENDER TOTAL footer rows — mirrors the on-screen summary
  // footer. Discount is applied to `boqGrandTotal` (already the uplifted
  // Σ column totals because cells were uplifted inline by
  // `generatePlotMatrixCategoryRows` above when `upliftFactor` was passed
  // in). When `discountPct` is 0 (no discount configured / not applied) we
  // skip both rows — the GRAND TOTAL row is already the tender total then.
  if (discountPct > 0) {
    const discountAmount = boqGrandTotal * (discountPct / 100);
    const tenderTotal = boqGrandTotal - discountAmount;

    // ─── DISCOUNT row ────────────────────────────────────────────────────
    currentRow++;
    try {
      worksheet.mergeCells(currentRow, 1, currentRow, totalColumns - 1);
    } catch (e) {
      console.error("Error merging plot matrix discount label cells:", e);
    }
    const discountLabelCell = worksheet.getCell(currentRow, 1);
    discountLabelCell.value = `DISCOUNT (${discountPct}%)`;
    discountLabelCell.font = { bold: true, size: 12, color: { argb: "000000" } };
    discountLabelCell.alignment = { horizontal: "left", vertical: "middle" };
    discountLabelCell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "E6F7FF" },
    };
    discountLabelCell.border = {
      top: { style: "thin", color: { argb: "000000" } },
      left: { style: "thin", color: { argb: "000000" } },
      bottom: { style: "thin", color: { argb: "000000" } },
      right: { style: "thin", color: { argb: "000000" } },
    };
    const discountAmtCell = worksheet.getCell(currentRow, totalColumns);
    discountAmtCell.value = `-£${discountAmount.toLocaleString("en-GB", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
    discountAmtCell.font = { bold: true, size: 12, color: { argb: "000000" } };
    discountAmtCell.alignment = { horizontal: "right", vertical: "middle" };
    discountAmtCell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "E6F7FF" },
    };
    discountAmtCell.border = {
      top: { style: "thin", color: { argb: "000000" } },
      left: { style: "thin", color: { argb: "000000" } },
      bottom: { style: "thin", color: { argb: "000000" } },
      right: { style: "thin", color: { argb: "000000" } },
    };
    worksheet.getRow(currentRow).height = 22;

    // ─── TENDER TOTAL row ────────────────────────────────────────────────
    currentRow++;
    try {
      worksheet.mergeCells(currentRow, 1, currentRow, totalColumns - 1);
    } catch (e) {
      console.error("Error merging plot matrix tender total cells:", e);
    }
    const tenderLabelCell = worksheet.getCell(currentRow, 1);
    tenderLabelCell.value = "TENDER TOTAL";
    tenderLabelCell.font = { bold: true, size: 13, color: { argb: "003366" } };
    tenderLabelCell.alignment = { horizontal: "left", vertical: "middle" };
    tenderLabelCell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "E6F7FF" },
    };
    tenderLabelCell.border = {
      top: { style: "medium", color: { argb: "000000" } },
      left: { style: "medium", color: { argb: "000000" } },
      bottom: { style: "medium", color: { argb: "000000" } },
      right: { style: "thin", color: { argb: "000000" } },
    };
    const tenderAmtCell = worksheet.getCell(currentRow, totalColumns);
    tenderAmtCell.value = `£${tenderTotal.toLocaleString("en-GB", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
    tenderAmtCell.font = { bold: true, size: 13, color: { argb: "003366" } };
    tenderAmtCell.alignment = { horizontal: "right", vertical: "middle" };
    tenderAmtCell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "E6F7FF" },
    };
    tenderAmtCell.border = {
      top: { style: "medium", color: { argb: "000000" } },
      left: { style: "thin", color: { argb: "000000" } },
      bottom: { style: "medium", color: { argb: "000000" } },
      right: { style: "medium", color: { argb: "000000" } },
    };
    worksheet.getRow(currentRow).height = 25;
  }

  // Auto-fit all columns to actual content so no text is hidden.
  // Only Plot No is capped (numbers are always short); all other columns size to their longest cell.
  {
    const colMinWidths: Record<number, number> = {};
    for (let i = 3; i <= totalColumns; i++) colMinWidths[i] = 14; // currency columns: never below 14
    autoFitColumns(worksheet, {
      columnMaxWidths: { 1: 12 },
      columnMinWidths: colMinWidths,
      defaultMin: 8,
      defaultMax: 100, // no effective cap — let content decide
      padding: 3,
    });
  }

  // Set row heights for better readability (apply to data rows only, skip header rows 1-7 and column header rows)
  worksheet.eachRow((row) => {
    if (
      row.number > 7 &&
      row.number !== mainHeaderRowNum &&
      row.number !== subHeaderRowNum
    ) {
      // Skip logo/header rows (1-7) and column header rows
      row.height = 20; // Standard height for all data rows in Plot Matrix
    }
  });

  // Enable text wrapping for data cells only (skip header rows 1-7 and column headers rows)
  worksheet.eachRow((row) => {
    if (
      row.number <= 7 ||
      row.number === mainHeaderRowNum ||
      row.number === subHeaderRowNum
    ) {
      // Skip header rows and column header rows - they have their own alignment set
      return;
    }
    row.eachCell((cell) => {
      // Skip cells that are part of merged ranges
      if (cell.isMerged) {
        return;
      }

      cell.alignment = {
        ...cell.alignment,
        wrapText: true,
        vertical: "middle",
        shrinkToFit: false,
      };
      // Ensure font is set to enable proper wrapping
      if (!cell.font || !cell.font.size) {
        cell.font = { ...cell.font, size: 11 };
      }
    });
  });

  // Set page setup for printing - fit to one page width on A3
  worksheet.pageSetup = {
    paperSize: 8, // A3 (larger paper size for better fit)
    orientation: "landscape", // Plot Matrix uses landscape for better column visibility
    fitToPage: true,
    fitToWidth: 1, // Fit all columns to one page width
    fitToHeight: 0, // Allow unlimited vertical pages
    scale: 100, // Full scale for A3 printing
    margins: {
      left: 0.2,
      right: 0.2,
      top: 0.3,
      bottom: 0.3,
      header: 0.15,
      footer: 0.15,
    },
    horizontalCentered: true,
  };
}

// Fixed ordered list of valuation stages for the Summary sheet.
// These must exactly match the main header derived from categoryName.split(" - ")[0]
// in the plot matrix category definitions in billSummaryCategories.ts.
const SUMMARY_STAGE_ORDER = [
  "Site Strip",
  "Cut/Fill",
  "Pile Matts",
  "Foundation Exc & Conc",
  "Foundations to DPC",
  "Foundation Floor",
  "Scaffold Platforms",
  "Plot Drainage",
  "Plot Services",
  "Private Drives to Binder",
  "Shared Drives to Binder",
  "Screen Walls",
  "Retaining Walls",
  "Service Connections",
  "Topsoil/Plot Clearance",
  "Private Drives to Surface Course",
  "Shared Drives to Surface Course",
];

/**
 * Plot Matrix Summary Sheet - one row per valuation stage with its total
 */
function createPlotMatrixSummarySheet(
  workbook: any,
  logoImageId: any,
  categoryColumns: any[],
  plotMatrixRows: any[],
  projectInfo: any,
  primaryColor: string,
  viewMode: 'plot' | 'stage' = 'plot',
  markup?: PlotMatrixMarkup,
) {
  const worksheet = workbook.addWorksheet("Summary");

  // Group dataIndexes by main valuation stage header (same split as Plot Matrix sheet)
  const groupedDataIndexes: { [mainHeader: string]: string[] } = {};
  categoryColumns.forEach((col: any) => {
    const mainHeader = (col.categoryName.split(" - ")[0] || col.categoryName).trim();
    if (!groupedDataIndexes[mainHeader]) {
      groupedDataIndexes[mainHeader] = [];
    }
    groupedDataIndexes[mainHeader].push(col.dataIndex);
  });

  // Build stage totals in the fixed display order — always show all 17 stages
  const stageTotals: { description: string; amount: number }[] = [];

  SUMMARY_STAGE_ORDER.forEach((stageName) => {
    const dataIndexes = groupedDataIndexes[stageName] || [];
    let stageTotal = 0;
    plotMatrixRows.forEach((row: any) => {
      dataIndexes.forEach((dataIndex) => {
        stageTotal += row[dataIndex]?.total || 0;
      });
    });
    // Round to 2dp — mirrors PlotMatrix.tsx renderSummaryRow per-column rounding
    // so the Summary sheet totals agree with the Plot Matrix sheet column totals.
    stageTotals.push({ description: stageName, amount: parseFloat(stageTotal.toFixed(2)) });
  });

  // Append any additional stages present in the data but not in the fixed order list
  Object.keys(groupedDataIndexes).forEach((mainHeader) => {
    if (!SUMMARY_STAGE_ORDER.includes(mainHeader)) {
      const dataIndexes = groupedDataIndexes[mainHeader];
      let stageTotal = 0;
      plotMatrixRows.forEach((row: any) => {
        dataIndexes.forEach((dataIndex) => {
          stageTotal += row[dataIndex]?.total || 0;
        });
      });
      stageTotals.push({ description: mainHeader, amount: parseFloat(stageTotal.toFixed(2)) });
    }
  });

  // Header rows (2 data columns: Description + Amount)
  const logoHeaderRowNum = createWorksheetHeader(
    worksheet,
    logoImageId,
    viewMode === 'stage' ? 'Valuation Stage Summary' : 'Plot Matrix - Summary',
    projectInfo.projectName,
    projectInfo.clientName,
    projectInfo.region,
    primaryColor,
    2,
    undefined,
    2, // logoMergeColumns = B column
  );

  // Column header row
  const colHeaderRow = logoHeaderRowNum;
  const colDefs = [
    { label: "Description", align: "left" as const },
    { label: "Amount", align: "right" as const },
  ];
  colDefs.forEach((def, idx) => {
    const cell = worksheet.getCell(colHeaderRow, idx + 1);
    cell.value = def.label;
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: primaryColor } };
    cell.font = { name: "Calibri", size: 12, bold: true, color: { argb: "FFFFFF" } };
    cell.alignment = { vertical: "middle", horizontal: def.align, wrapText: false };
    cell.border = {
      top: { style: "medium", color: { argb: "000000" } },
      left: { style: "medium", color: { argb: "000000" } },
      bottom: { style: "medium", color: { argb: "000000" } },
      right: { style: "medium", color: { argb: "000000" } },
    };
  });
  worksheet.getRow(colHeaderRow).height = 25;

  // Data rows
  let currentRow = colHeaderRow + 1;
  stageTotals.forEach((stage, idx) => {
    const isEven = idx % 2 === 0;
    const rowBg = isEven ? "F8F9FA" : "FFFFFF";

    const descCell = worksheet.getCell(currentRow, 1);
    descCell.value = stage.description;
    descCell.font = { name: "Calibri", size: 11 };
    descCell.alignment = { horizontal: "left", vertical: "middle" };
    descCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: rowBg } };
    descCell.border = {
      top: { style: "thin", color: { argb: "E5E5E5" } },
      left: { style: "medium", color: { argb: "000000" } },
      bottom: { style: "thin", color: { argb: "E5E5E5" } },
      right: { style: "thin", color: { argb: "CCCCCC" } },
    };

    const amtCell = worksheet.getCell(currentRow, 2);
    amtCell.value = `£${stage.amount.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    amtCell.font = { name: "Calibri", size: 11 };
    amtCell.alignment = { horizontal: "right", vertical: "middle" };
    amtCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: rowBg } };
    amtCell.border = {
      top: { style: "thin", color: { argb: "E5E5E5" } },
      left: { style: "thin", color: { argb: "CCCCCC" } },
      bottom: { style: "thin", color: { argb: "E5E5E5" } },
      right: { style: "medium", color: { argb: "000000" } },
    };

    worksheet.getRow(currentRow).height = 20;
    currentRow++;
  });

  // Spacer row
  currentRow++;

  // Grand Total row — sum of 2dp-rounded stage totals, then rounded to 2dp.
  // Mirrors boqGrandTotal in createPlotMatrixSheet so both sheets show the same value.
  const summaryGrandTotal = parseFloat(
    stageTotals.reduce((acc, s) => acc + s.amount, 0).toFixed(2),
  );
  const gtDescCell = worksheet.getCell(currentRow, 1);
  gtDescCell.value = "GRAND TOTAL";
  gtDescCell.font = { name: "Calibri", size: 12, bold: true, color: { argb: "003366" } };
  gtDescCell.alignment = { horizontal: "left", vertical: "middle" };
  gtDescCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFD700" } };
  gtDescCell.border = {
    top: { style: "medium", color: { argb: "000000" } },
    left: { style: "medium", color: { argb: "000000" } },
    bottom: { style: "medium", color: { argb: "000000" } },
    right: { style: "thin", color: { argb: "000000" } },
  };

  const gtAmtCell = worksheet.getCell(currentRow, 2);
  gtAmtCell.value = `£${summaryGrandTotal.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  gtAmtCell.font = { name: "Calibri", size: 12, bold: true, color: { argb: "003366" } };
  gtAmtCell.alignment = { horizontal: "right", vertical: "middle" };
  gtAmtCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFD700" } };
  gtAmtCell.border = {
    top: { style: "medium", color: { argb: "000000" } },
    left: { style: "thin", color: { argb: "000000" } },
    bottom: { style: "medium", color: { argb: "000000" } },
    right: { style: "medium", color: { argb: "000000" } },
  };
  worksheet.getRow(currentRow).height = 25;

  // DISCOUNT + TENDER TOTAL footer rows on the Summary sheet — mirrors the
  // Plot Matrix sheet (which already shows them just below its GRAND TOTAL
  // row). Skipped when no discount is applied; in that case the GRAND TOTAL
  // above already IS the tender total.
  const summaryDiscountPct = markup?.discountPct ?? 0;
  if (summaryDiscountPct > 0) {
    const summaryDiscountAmount = summaryGrandTotal * (summaryDiscountPct / 100);
    const summaryTenderTotal = summaryGrandTotal - summaryDiscountAmount;

    // DISCOUNT row
    currentRow++;
    const sDiscDesc = worksheet.getCell(currentRow, 1);
    sDiscDesc.value = `DISCOUNT (${summaryDiscountPct}%)`;
    sDiscDesc.font = { name: "Calibri", size: 12, bold: true, color: { argb: "000000" } };
    sDiscDesc.alignment = { horizontal: "left", vertical: "middle" };
    sDiscDesc.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "E6F7FF" } };
    sDiscDesc.border = {
      top: { style: "thin", color: { argb: "000000" } },
      left: { style: "thin", color: { argb: "000000" } },
      bottom: { style: "thin", color: { argb: "000000" } },
      right: { style: "thin", color: { argb: "000000" } },
    };
    const sDiscAmt = worksheet.getCell(currentRow, 2);
    sDiscAmt.value = `-£${summaryDiscountAmount.toLocaleString("en-GB", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
    sDiscAmt.font = { name: "Calibri", size: 12, bold: true, color: { argb: "000000" } };
    sDiscAmt.alignment = { horizontal: "right", vertical: "middle" };
    sDiscAmt.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "E6F7FF" } };
    sDiscAmt.border = {
      top: { style: "thin", color: { argb: "000000" } },
      left: { style: "thin", color: { argb: "000000" } },
      bottom: { style: "thin", color: { argb: "000000" } },
      right: { style: "thin", color: { argb: "000000" } },
    };
    worksheet.getRow(currentRow).height = 22;

    // TENDER TOTAL row
    currentRow++;
    const sTenderDesc = worksheet.getCell(currentRow, 1);
    sTenderDesc.value = "TENDER TOTAL";
    sTenderDesc.font = { name: "Calibri", size: 13, bold: true, color: { argb: "003366" } };
    sTenderDesc.alignment = { horizontal: "left", vertical: "middle" };
    sTenderDesc.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "E6F7FF" } };
    sTenderDesc.border = {
      top: { style: "medium", color: { argb: "000000" } },
      left: { style: "medium", color: { argb: "000000" } },
      bottom: { style: "medium", color: { argb: "000000" } },
      right: { style: "thin", color: { argb: "000000" } },
    };
    const sTenderAmt = worksheet.getCell(currentRow, 2);
    sTenderAmt.value = `£${summaryTenderTotal.toLocaleString("en-GB", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
    sTenderAmt.font = { name: "Calibri", size: 13, bold: true, color: { argb: "003366" } };
    sTenderAmt.alignment = { horizontal: "right", vertical: "middle" };
    sTenderAmt.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "E6F7FF" } };
    sTenderAmt.border = {
      top: { style: "medium", color: { argb: "000000" } },
      left: { style: "thin", color: { argb: "000000" } },
      bottom: { style: "medium", color: { argb: "000000" } },
      right: { style: "medium", color: { argb: "000000" } },
    };
    worksheet.getRow(currentRow).height = 25;
  }

  // Auto-fit columns; description gets a generous minimum, amount min 20, stretch to fill A4 portrait
  autoFitColumns(worksheet, {
    columnMaxWidths: { 1: 90, 2: 35 },
    columnMinWidths: { 1: 55, 2: 22 },
    defaultMin: 20,
    padding: 3,
    minTotalWidth: 100,
  });

  // Page setup
  worksheet.pageSetup = {
    paperSize: 9, // A4
    orientation: "portrait",
    fitToPage: true,
    fitToWidth: 1,
    fitToHeight: 0,
    margins: { left: 0.5, right: 0.5, top: 0.75, bottom: 0.75, header: 0.3, footer: 0.3 },
    horizontalCentered: true,
  };
}

/**
 * Standalone Plot Matrix Export
 * Exports only the Plot Matrix to Excel with proper formatting
 * Now accepts pre-calculated columns and rows from UI for exact match
 */
export async function exportPlotMatrixToExcel(
  groundworksData: any,
  projectInfo: {
    projectName: string;
    clientName: string;
    region: string;
    siteAddress?: string;
    locationData?: LocationData;
  },
  ratesMapping?: RateMappingJSON | null,
  itemLibraryData?: ItemLibraryResponse,
  preCalculatedColumns?: any[],
  preCalculatedRows?: any[],
  viewMode: 'plot' | 'stage' = 'plot',
  markup?: PlotMatrixMarkup,
) {
  try {
    const ExcelJS = await import("exceljs");
    const workbook = new ExcelJS.default.Workbook();

    const primaryColor = "003366"; // App's primary color

    // Add logo to workbook
    const logoImageId = workbook.addImage({
      base64: MNJLOGOBASE64,
      extension: "jpeg",
    });

    // Resolve columns and rows once so both sheets use identical data.
    // When the caller passes pre-calculated data (UI export path), the
    // columns already include "Uncategorized" and the rows already carry
    // the prelims+OH&P uplift — use as-is. Otherwise (defensive path)
    // append Uncategorized and apply the uplift here.
    const upliftFactor =
      (1 + (markup?.ohpPct ?? 0) / 100) * (1 + (markup?.prelimsPct ?? 0) / 100);
    const resolvedColumns =
      preCalculatedColumns ||
      generatePlotMatrixCategoryColumns(groundworksData);
    // preCalculatedRows (the normal UI export path) already carries the
    // Dynamic Bill Summary / fixed-summary extra-item `plotMapping` overlay
    // — see the matching comment in createPlotMatrixSheet above. Only the
    // defensive fresh-generation fallback needs it applied here.
    const resolvedRows =
      preCalculatedRows ||
      applyDynamicBillSummaryToPlotMatrixRows(
        generatePlotMatrixCategoryRows(
          groundworksData,
          resolvedColumns,
          ratesMapping,
          itemLibraryData,
          undefined,
          undefined,
          undefined,
          upliftFactor,
        ),
        resolvedColumns,
        groundworksData?.dynamicBillSummaryTabs ?? [],
        upliftFactor,
        groundworksData?.cutFillsExtraItems,
        groundworksData?.foundationsExtraItems,
        groundworksData?.drainageExtraItems,
        groundworksData?.plotDrivesExtraItems,
        groundworksData?.plotServiceExtraItems,
        groundworksData?.scaffoldExtraItems,
        groundworksData?.plotPavingsExtraItems,
        groundworksData?.gardenExtraItems,
        groundworksData?.retainingWallsExtraItems,
        groundworksData?.geoWallsExtraItems,
      );

    // Create Summary sheet first so it appears as the first tab
    createPlotMatrixSummarySheet(
      workbook,
      logoImageId,
      resolvedColumns,
      resolvedRows,
      projectInfo,
      primaryColor,
      viewMode,
      markup,
    );

    // Create Plot Matrix sheet (second tab)
    await createPlotMatrixSheet(
      workbook,
      logoImageId,
      groundworksData,
      projectInfo,
      primaryColor,
      ratesMapping,
      itemLibraryData,
      resolvedColumns,
      resolvedRows,
      viewMode,
      undefined,
      markup,
    );

    // Generate and download file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    const date = new Date().toISOString().split("T")[0];
    const filePrefix = viewMode === 'stage' ? 'Valuation_Stage_Matrix' : 'Plot_Matrix';
    link.download = `${filePrefix}_${projectInfo.projectName}_${date}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);

    return true;
  } catch (error) {
    console.error("Plot Matrix export error:", error);
    throw error;
  }
}

/**
 * Helper functions
 */

function calculateOverallGrandTotal(
  groundworksData: any,
  settings: any,
  includePreliminariesDisplay: boolean = false,
  includeOhpDisplay: boolean = false,
  includeDiscountDisplay: boolean = false,
  ratesMapping?: RateMappingJSON | null,
  itemLibraryData?: ItemLibraryResponse,
  persistedRetSpecs?: RetainingWallsBuildUpSpecification[],
  persistedGeoSpecs?: GeoToberWallBuildUpSpecification[],
): number {
  // Delegate to the same shared calculator as createSumAnalysisSheet (see
  // its own comment above) so the cover sheet's tender total includes every
  // domain's ad-hoc extra items and every Dynamic Bill Summary tab too,
  // instead of the duplicated switch statement this used to run that knew
  // about neither.
  const result = calculateSumAnalysisGrandTotal({
    groundworksData,
    ratesMapping,
    itemLibraryData,
    preliminariesPercent: settings?.groundworks?.preliminaries || 0,
    ohpPercent: settings?.groundworks?.overheadAndProfit || 0,
    discountPercent: settings?.groundworks?.discount || 0,
    includePreliminaries: includePreliminariesDisplay,
    includeOhp: includeOhpDisplay,
    includeDiscount: includeDiscountDisplay,
    enableLogging: false,
    retWallsBuildUpSpecs: persistedRetSpecs,
    geoToberWallBuildUpSpecs: persistedGeoSpecs,
  });

  return result.tenderTotal;
}

function buildHeaders(options: ExportDetailOptions) {
  const headers: string[] = [];
  const dataKeys: string[] = [];

  headers.push("Item");
  dataKeys.push("Item");

  if (options.includeDescriptions) {
    headers.push("Description");
    dataKeys.push("Description");
  }

  if (options.includeQuantities) {
    headers.push("Qty");
    dataKeys.push("Qty");
    headers.push("Unit");
    dataKeys.push("Unit");
  }

  if (options.includeRates) {
    headers.push("Rate");
    dataKeys.push("Rate");
  }

  if (options.includeTotals) {
    headers.push("Amount");
    dataKeys.push("Amount");
  }

  return { headers, dataKeys };
}

